import {
  ContextSelector,
  createContext,
  useContextSelector,
} from '@fluentui/react-context-selector';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import { isEqual } from 'lodash';
import { uniq } from 'lodash';
import { useRouter } from 'next/router';
import { Dispatch, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import InfiniteViewer from 'react-infinite-viewer';
import { createEditor, Transforms } from 'slate';
import { withReact } from 'slate-react';

import theme from '../assets/theme';
import {
  LOADING_SLIDE,
  LOADING_SLIDE_ID,
} from '../components/_molecules/DesignerSlideList/loadingSlide';
import {
  createGradeDropdownPromptInput,
  createNewImagePrompt,
  createNewPrompt,
  createPromptUniqueId,
  NewPromptTemplate,
} from '../components/_organisms/DesignerTabPane/PromptsTab/newPromptTemplates';
import { LeftMenuTabTypes } from '../components/_organisms/DesignerTabs/DesignerTabUtils';
import { PromptVariables } from '../components/dynamicPages/CreatePage/hooks/useLearningObjectiveRefinementHook';
import { RichTextEditorType } from '../components/SlideComponents/SlateRichTextEditor';
import {
  createSimpleSlateParagraphFromAIOutput,
  findAllSlateTextColors,
  isCuripodText,
  parseStringToSlateObject,
  selectAll,
  updatePromptInputElementLabelById,
  updatePromptOutputElementLabelById,
  withMathElements,
} from '../components/SlideComponents/slateUtil';
import {
  MediaMimeType,
  OnElementsChangeProps,
  Slide,
  SlideContentType,
  SlideElement,
  SlideType,
} from '../components/SlideComponents/slide.types';
import {
  colorIsHexCode,
  createNewEmptySlide,
  createPollElements,
  createSlideGroups,
  findRelatedSlidesWithIndex,
  findRelatedSlidesWithoutIndex,
  generateUniqueId,
  insertSlidesAfterSelectedSlide,
  insertSlidesBeforeSelectedSlide,
  isEmptySlide,
  isSlideElementAttachmentPassage,
  isSlideElementText,
  MAX_ZOOM,
  MIN_ZOOM,
  RICHTEXTEDITOR_ID_PREFIX,
  SLIDE_ASPECT,
} from '../components/SlideComponents/slideUtil';
import { makeNewIdsForSlide } from '../components/SlideComponents/slideUtilCopyPaste';
import { useHideIntercom } from '../providers/CustomIntercomStateProvider';
import { LessonAttachment } from '../services/backendService/backendService';
import {
  ActiveGame,
  ById,
  Prompt,
  PromptInput,
  PromptInputData,
  PromptMessageRole,
  PromptOutput,
  PromptOutputProperty,
  PromptSingleTestCaseResult,
  PromptSingleTestCaseResultType,
  RoundMedia,
} from '../services/backendService/types';
import {
  AiContent,
  Template,
  TemplateSettings,
} from '../services/TemplateService/Template.type';
import { MOBILE_TO_DESKTOP_BREAKPOINT_PX } from '../utils/creator';
import { isSlideType } from '../utils/roundUtils';
import { stampSlidesPremiumContent } from '../utils/saveGeneratedLessonAsFreeUtils';
import { NEW_TEMPLATE_PARAM } from '../utils/urlUtils';
import useAccessBadge from './useAccessBadge';
import { useBreakPoint } from './useBreakPoint';
import { useFeatureFlag } from './useFeatureFlag';
import useRouterMatch from './useRouterMatch';
import useScopeAndSequenceAutoCopy from './useScopeAndSequenceAutoCopy';
import { useRawTemplate } from './useTemplate';
import { useTemplateAccessHandler } from './useTemplateAccessHandler';

// ─── Helper ──────────────────────────────────────────────────────────────────
function isInfiniteViewerReady(viewer: InfiniteViewer | null | undefined): boolean {
  if (!viewer) return false;
  try {
    viewer.getZoom();
    return true;
  } catch {
    return false;
  }
}

// ─── Types ───────────────────────────────────────────────────────────────────

export type TabState = {
  tab: LeftMenuTabTypes;
  subTab?: 'newBlankSlide' | 'appSmash' | 'editAttachment' | 'newAttachment';
  extraState?: string;
  previousTab?: LeftMenuTabTypes;
  initialData?: {
    showActivityOptions?: boolean;
    droppedImageData?: { src: string; mimeType: string };
    agentPrompt?: string;
  };
  dataState?: {
    attachmentTab?: {
      attachmentId?: string;
    };
  };
};

export type ElementTransformationProperties = {
  id: string;
  transformX: number;
  transformY: number;
  transformWidth: number;
  transformHeight: number;
  transformAngle: number;
  transformScale?: number;
};

export type LoaderState = {
  startTime: number;
};

export type ErrorDTO<T = unknown> = {
  error: {
    code: number;
    message: string;
    type: string;
    errors?: T[];
  };
};

export type GeneratorCategory = {
  name: string;
  value: string;
};

type ForeceLoadingState = 'loading' | 'success' | undefined;

export type DesignerUIState = {
  selectedTab: TabState;
  isDesktop: boolean;
  forceLoadingState: ForeceLoadingState;
  isUserIdle: boolean;
  isLeftTabCollapsed: boolean;
  isSlideListCollapsed: boolean;
  showMediaModal: boolean;
  isDrawerOpen: boolean;
  uploadedSlidesCache?: Slide[];
  textElementIdBeingEdited: string | undefined;
  mcExplanationEditingOptionId: string | undefined;
  editClickPosition: { clientX: number; clientY: number; ts: number } | undefined;
  elementTransformationProperties: ElementTransformationProperties[] | undefined;
  anElementIsBeingTransformed: boolean;
  selectedTargetElements: string[];
  canInteractWithIframe: boolean;
  previewAttachmentPassageElementId: string | undefined;
  showTabPaneFullWidth: boolean;
  selectedElements: SlideElement[] | null;
  multiSelectedSlides: string[];
  selectedPromptId: string | undefined;
  richTextEditor: RichTextEditorType | null;
  selectedRichTextEditors: RichTextEditorType[];
  isArrowKeyNavigationDisabled: boolean;
  zoom: number;
  aiConnection?: {
    aiTextElementId?: string;
    aiImageElementId?: string;
    promptSourceId?: string;
    promptImageSourceId?: string;
    isPollOption?: boolean;
  };
  selectedTestcaseResult?: {
    id: string;
    inputs: PromptInputData[];
    slides: Slide[];
    allTestcasePromptOutputs?: PromptSingleTestCaseResult;
    lessonAttachments?: LessonAttachment[];
    error?: string;
  };
  promptDependentInputs?: PromptSingleTestCaseResult | undefined;
  testcaseResults?: {
    id: string;
    inputs: PromptInputData[];
    slides: Slide[];
    allTestcasePromptOutputs?: PromptSingleTestCaseResult;
    lessonAttachments?: LessonAttachment[];
    isSuggestion: boolean;
  }[];
  templateAccessErrorModal: {
    isVisible: boolean;
    errorType: 'not_found' | 'access_denied' | 'generic_error' | null;
  };
  copyLessonModal: {
    isVisible: boolean;
    templateMetadata?: Template['metadata'];
    /** S&S attribution to stamp onto the copy when a manual copy is made. */
    stampMetadata?: Template['metadata'];
  };
  isAutoCopyingLesson?: boolean;
  isPresentOrPreviewModalVisible: boolean;
  isVariantPlaceholderLinkModalVisible: boolean;
  hoveredRetrySourceSlideId?: string;
  agent: {
    isStreaming: boolean;
    error: ErrorDTO | null;
    isComplete: boolean;
    state: 'thinking' | 'generating' | null;
  };
  initialLessonBuilding?: {
    isLoading: boolean;
    params: PromptVariables;
    started: boolean;
  };
  loaderStates: Record<string, LoaderState>;
};

export type TemplateState = Template | undefined;

// The snapshot stored in undo/redo stacks (everything except the stacks themselves)
export type DesignerSnapshot = {
  ui: DesignerUIState;
  template: TemplateState;
};

export type DesignerHistory = {
  canUndo: boolean;
  canRedo: boolean;
  undoStack: DesignerSnapshot[];
  redoStack: DesignerSnapshot[];
};

export type DesignerState = {
  ui: DesignerUIState;
  template: TemplateState;
  isFetchingTemplate: boolean;
  history: DesignerHistory;
};

// ─── Action Enum ─────────────────────────────────────────────────────────────

export enum DesignerActions {
  // === UI actions ===
  setSelectedTab = 'SET_SELECTED_TAB',
  setSelectedTabGoBack = 'SET_SELECTED_TAB_GO_BACK',
  clearDroppedImageData = 'CLEAR_DROPPED_IMAGE_DATA',
  setIsDesktop = 'SET_IS_DESKTOP',
  setLeftTabCollapsed = 'SET_LEFT_TAB_COLLAPSED',
  setSlideListCollapsed = 'SET_SLIDE_LIST_COLLAPSED',
  setShowTabPaneFullWidth = 'SET_SHOW_TAB_PANE_FULL_WIDTH',
  toggleMediaModal = 'TOGGLE_MEDIA_MODAL',
  closeDrawer = 'CLOSE_DRAWER',
  selectNewSlide = 'SELECT_NEW_SLIDE',
  selectEditMedia = 'SELECT_EDIT_MEDIA',
  selectSlide = 'SELECT_SLIDE',
  setForceLoadingState = 'SET_FORCE_LOADING_STATE',
  setIsUserIdle = 'SET_IS_USER_IDLE',
  startEditingTextElement = 'START_EDITING_TEXT_ELEMENT',
  stopEditingTextElement = 'STOP_EDITING_TEXT_ELEMENT',
  openMcExplanationEditor = 'OPEN_MC_EXPLANATION_EDITOR',
  closeMcExplanationEditor = 'CLOSE_MC_EXPLANATION_EDITOR',
  changeTextElementBeingEdited = 'CHANGE_EDITING_TEXT_ELEMENT',
  transformElements = 'TRANSFORM_ELEMENTS',
  stopTransformingElement = 'STOP_TRANSFORMING_ELEMENT',
  selectTargetElements = 'SELECT_TARGETS',
  deselectElements = 'DESELECT_ELEMENT',
  setZoom = 'SET_ZOOM',
  clearTextElementHeight = 'CLEAR_TEXT_ELEMENT_HEIGHT',
  allowInteractionWithIframe = 'ALLOW_INTERACTION_WITH_IFRAME',
  previewAttachmentPassage = 'PREVIEW_ATTACHMENT_PASSAGE',
  closeAttachmentPassagePreview = 'CLOSE_ATTACHMENT_PASSAGE_PREVIEW',
  selectElements = 'SELECT_ELEMENTS',
  updateSelectedElements = 'UPDATE_SELECTED_ELEMENTS',
  setSelectedPromptId = 'SET_SELECTED_PROMPT_ID',
  disableArrowKeyNavigation = 'DISABLE_ARROW_KEY_NAVIGATION',
  setTouchDeviceElementClicked = 'SET_TOUCH_DEVICE_ELEMENT_CLICKED',
  enableArrowKeyNavigation = 'ENABLE_ARROW_KEY_NAVIGATION',
  setAITextElementId = 'SET_AI_TEXT_ELEMENT_ID',
  clearAiElements = 'CLEAR_AI_ELEMENTS',
  setAIImageElementId = 'SET_AI_IMAGE_ELEMENT_ID',
  setAITextPromptSourceId = 'SET_AI_TEXT_PROMPT_SOURCE_ID',
  setAIImagePromptSourceId = 'SET_AI_IMAGE_PROMPT_SOURCE_ID',
  resetAIConnection = 'RESET_AI_TEXT_CONNECTION',
  showTemplateAccessErrorModal = 'SHOW_TEMPLATE_ACCESS_ERROR_MODAL',
  showCopyLessonModal = 'SHOW_COPY_LESSON_MODAL',
  setIsAutoCopyingLesson = 'SET_IS_AUTO_COPYING_LESSON',
  hideCopyLessonModal = 'HIDE_COPY_LESSON_MODAL',
  showPresentOrPreviewModal = 'SHOW_PRESENT_OR_PREVIEW_MODAL',
  hidePresentOrPreviewModal = 'HIDE_PRESENT_OR_PREVIEW_MODAL',
  showVariantPlaceholderLinkModal = 'SHOW_VARIANT_PLACEHOLDER_LINK_MODAL',
  hideVariantPlaceholderLinkModal = 'HIDE_VARIANT_PLACEHOLDER_LINK_MODAL',
  setHoveredRetrySourceSlideId = 'SET_HOVERED_RETRY_SOURCE_SLIDE_ID',
  setTestcaseResults = 'SET_TESTCASE_RESULTS',
  setSelectedTestcaseResult = 'SET_SELECTED_TESTCASE_RESULTS',
  updatePromptDependentInputs = 'UPDATE_PROMPT_DEPENDENT_INPUTS',
  updatePromptDependentInputsBulk = 'UPDATE_PROMPT_DEPENDENT_INPUTS_BULK',
  agentStreamStarted = 'SET_AGENT_STREAM_STARTED',
  agentReset = 'RESET_AGENT',
  agentStreamStopped = 'SET_AGENT_STREAM_STOPPED',
  agentStreamError = 'SET_AGENT_STREAM_ERROR',
  agentStreamOnStateChange = 'SET_AGENT_STREAM_ON_STATE_CHANGE',
  agentStreamOnComplete = 'SET_AGENT_STREAM_ON_COMPLETE',
  agentOnCompleteMessageReceived = 'SET_AGENT_ON_COMPLETE_MESSAGE_RECEIVED',
  startInitialLessonBuilding = 'START_INITIAL_LESSON_BUILDING',
  completeInitialLessonBuilding = 'COMPLETE_INITIAL_LESSON_BUILDING',
  initLoaderState = 'INIT_LOADER_STATE',
  slideListSelectionChanged = 'SLIDE_LIST_SELECTION_CHANGED',
  clearMultiSelectedSlides = 'CLEAR_MULTI_SELECTED_SLIDES',
  setMultiSelectedSlides = 'SET_MULTI_SELECTED_SLIDES',
  slideDragStarted = 'SLIDE_DRAG_STARTED',

  // === Template actions ===
  initTemplate = 'INIT',
  changeSelectedSlide = 'CHANGE_SELECTED_SLIDE',
  deleteSlide = 'DELETE_SLIDE',
  deleteSlides = 'DELETE_SLIDES',
  initActiveGame = 'INIT_ACTIVE_GAME',
  initSetFirstSlide = 'INIT_SET_FIRST_SLIDE',
  nextSlide = 'NEXT_SLIDE',
  previousSlide = 'PREVIOUS_SLIDE',
  copySlide = 'COPY_SLIDE',
  endGame = 'END_GAME',
  changeSlide = 'CHANGE_SLIDE',
  addSlide = 'ADD_SLIDE',
  addTranslatedSlides = 'ADD_TRANSLATED_SLIDES',
  slidesDragged = 'SLIDES_DRAGGED',
  allSlidesChanged = 'ALL_SLIDES_CHANGED',
  agentUpdatedState = 'AGENT_UPDATED_STATE',
  setBackgroundOnAllSlides = 'SET_BACKGROUND_ON_ALL_SLIDES',
  titleChanged = 'TITLE_CHANGED',
  descriptionChanged = 'DESCRIPTION_CHANGED',
  coverImageAltTextChanged = 'COVER_IMAGE_ALT_TEXT_CHANGED',
  languageChanged = 'LANGUAGE_CHANGED',
  parentIdChanged = 'PARENT_ID_CHANGED',
  changeSubjects = 'CHANGE_SUBJECTS',
  changeGrades = 'CHANGE_GRADES',
  realNamesChanged = 'REAL_NAMES_CHANGED',
  contentFilteringChanged = 'CONTENT_FILTERING_CHANGED',
  coverPhotoChanged = 'COVER_PHOTO_CHANGED',
  sharingPublicChanged = 'SHARE_PUBLIC_CHANGED',
  isBlockedFromSharingChanged = 'IS_BLOCKED_FROM_SHARING_CHANGED',
  isPrivateChanged = 'IS_PRIVATE_CHANGED',
  isPremiumContentChanged = 'IS_PREMIUM_CONTENT_CHANGED',
  addPrompt = 'ADD_PROMPT',
  addImagePrompt = 'ADD_IMAGE_PROMPT',
  editPrompt = 'EDIT_PROMPT',
  duplicatePrompt = 'DUPLICATE_PROMPT',
  deletePrompt = 'DELETE_PROMPT',
  addPromptInput = 'ADD_PROMPT_INPUT',
  deletePromptInput = 'DELETE_PROMPT_INPUT',
  editPromptInput = 'EDIT_PROMPT_INPUT',
  changePromptMessageRole = 'CHANGE_PROMPT_MESSAGE_ROLE',
  changePromptOutputType = 'CHANGE_PROMPT_OUTPUT_TYPE',
  addPromptOutputProperty = 'ADD_PROMPT_OUTPUT_PROPERTY',
  addTestCase = 'ADD_TEST_CASE',
  editPromptOutputProperty = 'EDIT_PROMPT_OUTPUT_PROPERTY',
  deletePromptOutputProperty = 'DELETE_PROMPT_OUTPUT_PROPERTY',
  convertTextElementToAITextElement = 'CONVERT_TEXT_ELEMENT_TO_AI_TEXT_ELEMENT',
  convertImageElementToAIImageElement = 'CONVERT_IMAGE_ELEMENT_TO_AI_IMAGE_ELEMENT',
  editGeneratorCategories = 'EDIT_GENERATOR_CATEGORIES',
  addCurifyPlaceholderSlides = 'ADD_CURIFY_PLACEHOLDER_SLIDES',
  attachmentCreated = 'ATTACHMENT_CREATED',
  attachmentUpserted = 'ATTACHMENT_UPSERTED',
  attachmentEdited = 'ATTACHMENT_EDITED',
  attachmentDeleted = 'ATTACHMENT_DELETED',
  metadataPropertyChanged = 'METADATA_PROPERTY_CHANGED',
  settingsPropertyChanged = 'SETTINGS_PROPERTY_CHANGED',
  slideVariationDesignation = 'SLIDE_VARIATION_DESIGNATION',

  // === Cross-cutting actions ===
  slideSelected = 'SLIDE_SELECTED',
  createNewSlide = 'CREATE_NEW_SLIDE',
  slideTypeChanged = 'SLIDE_TYPE_CHANGED',
  elementsChanged = 'ELEMENTS_CHANGED',
  elementsAdded = 'ELEMENTS_ADDED',
  elementsDeleted = 'ELEMENTS_DELETED',
  undo = 'UNDO',
  redo = 'REDO',
}

// ─── Action Types ────────────────────────────────────────────────────────────

export type DesignerAction = { skipHistory?: boolean } & ( // UI actions
  | { type: DesignerActions.setSelectedTab; payload: TabState }
  | { type: DesignerActions.setSelectedTabGoBack }
  | { type: DesignerActions.clearDroppedImageData }
  | { type: DesignerActions.setIsDesktop; payload: boolean }
  | { type: DesignerActions.toggleMediaModal }
  | { type: DesignerActions.closeDrawer }
  | { type: DesignerActions.selectNewSlide }
  | { type: DesignerActions.selectSlide; payload: Slide }
  | { type: DesignerActions.setForceLoadingState; payload: ForeceLoadingState }
  | { type: DesignerActions.setIsUserIdle; payload: boolean }
  | {
      type: DesignerActions.selectEditMedia;
      payload: { src: string; mimeType: string } | undefined;
    }
  | {
      type: DesignerActions.startEditingTextElement;
      elementId: string;
      clickPosition?: { clientX: number; clientY: number };
      selectAll?: boolean;
    }
  | { type: DesignerActions.stopEditingTextElement }
  | { type: DesignerActions.openMcExplanationEditor; optionId: string }
  | { type: DesignerActions.closeMcExplanationEditor }
  | { type: DesignerActions.changeTextElementBeingEdited; elementId: string }
  | {
      type: DesignerActions.transformElements;
      elementTransformationProperties: ElementTransformationProperties[];
      shouldSetTransformingElement: boolean;
    }
  | { type: DesignerActions.stopTransformingElement }
  | { type: DesignerActions.setTouchDeviceElementClicked; isTouched: boolean }
  | { type: DesignerActions.selectTargetElements; selectedTargetElements: string[] }
  | { type: DesignerActions.setLeftTabCollapsed; isCollapsed: boolean }
  | { type: DesignerActions.setSlideListCollapsed; isCollapsed: boolean }
  | { type: DesignerActions.setShowTabPaneFullWidth; isExpanded: boolean }
  | { type: DesignerActions.deselectElements }
  | {
      type: DesignerActions.setZoom;
      zoom?: number;
      zoomAction?: 'fit' | 'increase' | 'decrease';
      containerFitWidth?: number;
      containerFitHeight?: number;
      infiniteViewer?: InfiniteViewer;
    }
  | { type: DesignerActions.clearTextElementHeight }
  | { type: DesignerActions.allowInteractionWithIframe }
  | { type: DesignerActions.previewAttachmentPassage; elementId: string }
  | { type: DesignerActions.closeAttachmentPassagePreview }
  | { type: DesignerActions.selectElements; selectedElements: SlideElement[] | null }
  | { type: DesignerActions.updateSelectedElements; selectedElements: SlideElement[] }
  | { type: DesignerActions.setSelectedPromptId; selectedPromptId: string | undefined }
  | { type: DesignerActions.disableArrowKeyNavigation }
  | { type: DesignerActions.enableArrowKeyNavigation }
  | {
      type: DesignerActions.setAITextElementId;
      elementId: string;
      isPollOption?: boolean;
    }
  | { type: DesignerActions.setAIImageElementId; elementId: string }
  | { type: DesignerActions.clearAiElements }
  | { type: DesignerActions.setAITextPromptSourceId; promptSourceId: string }
  | { type: DesignerActions.setAIImagePromptSourceId; promptImageSourceId: string }
  | { type: DesignerActions.resetAIConnection }
  | {
      type: DesignerActions.showTemplateAccessErrorModal;
      errorType: 'not_found' | 'access_denied' | 'generic_error';
    }
  | {
      type: DesignerActions.showCopyLessonModal;
      payload?: {
        templateMetadata?: Template['metadata'];
        stampMetadata?: Template['metadata'];
      };
    }
  | { type: DesignerActions.setIsAutoCopyingLesson; payload: boolean }
  | { type: DesignerActions.hideCopyLessonModal }
  | { type: DesignerActions.showPresentOrPreviewModal }
  | { type: DesignerActions.hidePresentOrPreviewModal }
  | { type: DesignerActions.showVariantPlaceholderLinkModal }
  | { type: DesignerActions.hideVariantPlaceholderLinkModal }
  | {
      type: DesignerActions.setHoveredRetrySourceSlideId;
      slideId: string | undefined;
    }
  | {
      type: DesignerActions.setTestcaseResults;
      payload: {
        id: string;
        inputs: PromptInputData[];
        slides: Slide[];
        lessonAttachments?: LessonAttachment[];
        allTestcasePromptOutputs?: PromptSingleTestCaseResult;
        isSuggestion: boolean;
      }[];
    }
  | {
      type: DesignerActions.setSelectedTestcaseResult;
      payload: {
        id: string;
        inputs: PromptInputData[];
        slides: Slide[];
        lessonAttachments?: LessonAttachment[];
        error?: string;
      };
    }
  | {
      type: DesignerActions.updatePromptDependentInputs;
      payload: { id: string; value: string; type: PromptSingleTestCaseResultType };
    }
  | {
      type: DesignerActions.updatePromptDependentInputsBulk;
      payload: { id: string; value: string; type: PromptSingleTestCaseResultType }[];
    }
  | { type: DesignerActions.agentStreamStarted }
  | { type: DesignerActions.agentReset }
  | { type: DesignerActions.agentStreamStopped }
  | { type: DesignerActions.agentStreamError; payload: ErrorDTO }
  | {
      type: DesignerActions.agentStreamOnStateChange;
      payload: { state: 'thinking' | 'generating' | null };
    }
  | { type: DesignerActions.agentStreamOnComplete; payload: { isComplete: boolean } }
  | { type: DesignerActions.startInitialLessonBuilding }
  | { type: DesignerActions.completeInitialLessonBuilding }
  | { type: DesignerActions.initLoaderState; payload: { gameId: string } }
  | {
      type: DesignerActions.slideListSelectionChanged;
      slideId: string;
      modifiers?: { shiftKey?: boolean; metaKey?: boolean; ctrlKey?: boolean };
    }
  | { type: DesignerActions.clearMultiSelectedSlides }
  | { type: DesignerActions.setMultiSelectedSlides; slideIds: string[] }
  | { type: DesignerActions.slideDragStarted; slideId: string }

  // Template actions
  | { type: DesignerActions.initTemplate; template: Template }
  | { type: DesignerActions.changeSelectedSlide; slideId: string }
  | { type: DesignerActions.deleteSlide; slideId: string }
  | { type: DesignerActions.deleteSlides; slideIds: string[] }
  | { type: DesignerActions.initActiveGame; activeGame: ActiveGame }
  | { type: DesignerActions.initSetFirstSlide }
  | { type: DesignerActions.nextSlide }
  | { type: DesignerActions.previousSlide }
  | { type: DesignerActions.copySlide; slideId: string }
  | { type: DesignerActions.endGame }
  | { type: DesignerActions.changeSlide; slide: Slide }
  | { type: DesignerActions.addSlide; slides: Slide[]; replace: boolean }
  | {
      type: DesignerActions.addTranslatedSlides;
      slides: Slide[];
      lessonAttachments: LessonAttachment[];
      roundsToTranslate: { name: string; slideId: string }[];
    }
  | { type: DesignerActions.slidesDragged; slides: Slide[] }
  | { type: DesignerActions.allSlidesChanged; slides: Slide[]; selectedSlideId: string }
  | {
      type: DesignerActions.agentUpdatedState;
      slides: Slide[];
      selectedSlideId: string;
      attachments: LessonAttachment[];
      metadata: ById<string | number | boolean> | null | undefined;
      keepCurrentSlide?: boolean;
    }
  | { type: DesignerActions.setBackgroundOnAllSlides; media: RoundMedia }
  | { type: DesignerActions.titleChanged; value?: string }
  | { type: DesignerActions.descriptionChanged; value?: string }
  | { type: DesignerActions.coverImageAltTextChanged; value?: string }
  | { type: DesignerActions.languageChanged; language: string }
  | { type: DesignerActions.parentIdChanged; parentId: string | null; isPrivate: boolean }
  | { type: DesignerActions.realNamesChanged; require: boolean }
  | { type: DesignerActions.contentFilteringChanged; isEnabled: boolean }
  | {
      type: DesignerActions.sharingPublicChanged;
      templateSharingPublic: boolean;
    }
  | {
      type: DesignerActions.metadataPropertyChanged;
      payload: { key: string; value: string | number | boolean };
    }
  | {
      type: DesignerActions.settingsPropertyChanged;
      payload: { key: keyof TemplateSettings; value: string | number | boolean };
    }
  | { type: DesignerActions.isBlockedFromSharingChanged; isBlockedFromSharing: boolean }
  | { type: DesignerActions.isPrivateChanged; isPrivate: boolean }
  | { type: DesignerActions.isPremiumContentChanged; isPremiumContent: boolean }
  | {
      type: DesignerActions.coverPhotoChanged;
      coverImageId: string;
      coverImageAltText?: string;
    }
  | {
      type: DesignerActions.addPrompt;
      promptName: string;
      newPromptId: string;
      newPromptTemplate?: NewPromptTemplate;
    }
  | {
      type: DesignerActions.addImagePrompt;
      promptName: string;
      newPromptId: string;
      newPromptTemplate?: NewPromptTemplate;
    }
  | { type: DesignerActions.deletePrompt; promptId: string }
  | { type: DesignerActions.duplicatePrompt; promptId: string }
  | { type: DesignerActions.editPrompt; prompt: Prompt }
  | { type: DesignerActions.addPromptInput; promptInput?: PromptInput }
  | { type: DesignerActions.editPromptInput; promptInput: PromptInput }
  | { type: DesignerActions.deletePromptInput; inputId: string }
  | {
      type: DesignerActions.changePromptMessageRole;
      role: PromptMessageRole;
      messageIndex: number;
    }
  | {
      type: DesignerActions.changePromptOutputType;
      outputType: 'string' | 'object' | 'array';
      promptId: string;
    }
  | { type: DesignerActions.addPromptOutputProperty; promptId: string }
  | {
      type: DesignerActions.editPromptOutputProperty;
      promptId: string;
      promptOutputProperty: PromptOutputProperty;
      promptOutputPropertyOrder?: string[];
    }
  | {
      type: DesignerActions.deletePromptOutputProperty;
      promptId: string;
      promptOutputPropertyId: string;
    }
  | {
      type: DesignerActions.convertTextElementToAITextElement;
      elementId: string;
      promptSourceId: string;
      exampleText: string;
      isPollOption?: boolean;
    }
  | {
      type: DesignerActions.convertImageElementToAIImageElement;
      elementId: string;
      promptImageSourceId: string;
      exampleImage: {
        id?: string;
        mimetype: MediaMimeType;
        url?: string;
        thumbnail?: string;
        downloadLocation?: string;
      };
    }
  | { type: DesignerActions.editGeneratorCategories; categories: GeneratorCategory[] }
  | { type: DesignerActions.changeSubjects; subjects: string[] }
  | { type: DesignerActions.changeGrades; grades: string[] }
  | { type: DesignerActions.addCurifyPlaceholderSlides; promptInputId: string }
  | { type: DesignerActions.attachmentCreated; attachment: LessonAttachment }
  | { type: DesignerActions.attachmentUpserted; attachment: LessonAttachment }
  | { type: DesignerActions.attachmentEdited; attachment: LessonAttachment }
  | { type: DesignerActions.attachmentDeleted; attachmentId: string }
  | { type: DesignerActions.slideVariationDesignation; slide: Slide }

  // Cross-cutting actions
  | { type: DesignerActions.slideSelected; slideId: string }
  | {
      type: DesignerActions.createNewSlide;
      combinedType: SlideType | SlideContentType | 'blank';
      replace?: boolean;
    }
  | {
      type: DesignerActions.slideTypeChanged;
      combinedType: SlideType | SlideContentType;
      replace?: boolean;
    }
  | ({ type: DesignerActions.elementsChanged } & OnElementsChangeProps)
  | {
      type: DesignerActions.elementsAdded;
      elements: SlideElement[];
      elementsToRemove?: SlideElement[];
    }
  | { type: DesignerActions.elementsDeleted; elements: SlideElement[] }
  | { type: DesignerActions.undo }
  | { type: DesignerActions.redo }
);

// ─── Pure helpers ────────────────────────────────────────────────────────────

const emptyAIContent: AiContent = {
  categories: [],
  promptInputs: [
    createGradeDropdownPromptInput(),
    {
      id: `input-topic`,
      label: 'Topic',
      order: 0,
      type: 'shortText',
      allowEmpty: false,
      isRequired: true,
    },
  ],
  prompts: [],
  promptInputSuggestions: [],
};

function duplicateSlideHelper(
  localTemplate: TemplateState,
  slideId: string,
): TemplateState {
  if (!localTemplate || !localTemplate.content.slides) return localTemplate;
  const slide = localTemplate.content.slides.find(s => s.id === slideId);
  if (!slide) return localTemplate;

  let newSlides: Slide[] = [];
  if (slide.type === 'slide') {
    const newSlide: Slide = { ...slide, id: generateUniqueId().toString() };
    newSlides = [newSlide];
  } else {
    const relatedSlides = findRelatedSlidesWithoutIndex(
      slide.id,
      localTemplate.content.slides,
    );
    newSlides = makeNewIdsForSlide(relatedSlides).map((s, i) => {
      const original = relatedSlides[i];
      if (original?.retryOfSlideId && !s.retryOfSlideId) {
        return { ...s, retryOfSlideId: original.retryOfSlideId };
      }
      return s;
    });
  }
  return {
    ...localTemplate,
    content: {
      ...localTemplate.content,
      slides: insertSlidesAfterSelectedSlide(
        localTemplate.content.slides,
        newSlides,
        slide.id,
      ),
    },
    currentRound: newSlides[0].id,
  };
}

function previousSlideHelper(localTemplate: TemplateState): TemplateState {
  if (!localTemplate || !localTemplate.content.slides) return localTemplate;
  const selectedSlide = localTemplate.content.slides.find(
    s => s.id === localTemplate.currentRound,
  );
  if (!selectedSlide) return localTemplate;
  const currentSlideIndex = localTemplate.content.slides.findIndex(
    r => r.id === selectedSlide.id,
  );
  if (currentSlideIndex === -1)
    return { ...localTemplate, currentRound: localTemplate.content.slides[0].id };
  if (currentSlideIndex === 0) return localTemplate;
  const nextSlide = localTemplate.content.slides[currentSlideIndex - 1];
  if (!nextSlide) return localTemplate;
  const relatedSlides = findRelatedSlidesWithoutIndex(
    nextSlide.id,
    localTemplate.content.slides,
  );
  return { ...localTemplate, currentRound: relatedSlides[0].id };
}

function nextSlideHelper(localTemplate: TemplateState): TemplateState {
  if (!localTemplate || !localTemplate.content.slides) return localTemplate;
  const selectedSlide = localTemplate.content.slides.find(
    s => s.id === localTemplate.currentRound,
  );
  if (!selectedSlide) return localTemplate;
  const currentSlideIndex = localTemplate.content.slides.findIndex(
    r => r.id === selectedSlide.id,
  );
  if (currentSlideIndex === -1)
    return { ...localTemplate, currentRound: localTemplate.content.slides[0].id };
  if (currentSlideIndex === localTemplate.content.slides.length - 1) return localTemplate;
  const currentRelatedSlides = findRelatedSlidesWithIndex(
    selectedSlide.id,
    localTemplate.content.slides,
  );
  const nextRound =
    localTemplate.content.slides[
      currentRelatedSlides[currentRelatedSlides.length - 1].index
    ];
  if (!nextRound) return localTemplate;
  const relatedSlides = findRelatedSlidesWithoutIndex(
    nextRound.id,
    localTemplate.content.slides,
  );
  return { ...localTemplate, currentRound: relatedSlides[0].id };
}

function deleteSlideHelper(localTemplate: TemplateState, slideId: string): TemplateState {
  if (!localTemplate || !localTemplate.content.slides || !localTemplate.currentRound)
    return localTemplate;
  const relatedSlides = findRelatedSlidesWithoutIndex(
    slideId,
    localTemplate.content.slides,
  );
  const index = localTemplate.content.slides.findIndex(r => r.id === relatedSlides[0].id);
  let newSelectedSlideId: string | null = null;
  if (index === 0) {
    newSelectedSlideId = localTemplate.content.slides[0].id;
  } else {
    const nextSelectedSlide = localTemplate.content.slides[index - 1];
    const nextRelatedSlides = findRelatedSlidesWithoutIndex(
      nextSelectedSlide.id,
      localTemplate.content.slides,
    );
    newSelectedSlideId = nextRelatedSlides[0].id;
  }
  const ids = relatedSlides.map(r => r.id);
  const newSlides = (localTemplate.content.slides || []).filter(r => !ids.includes(r.id));
  return {
    ...localTemplate,
    content: { ...localTemplate.content, slides: newSlides },
    currentRound: newSelectedSlideId,
  };
}

function deleteSlidesHelper(
  localTemplate: TemplateState,
  slideIds: string[],
): TemplateState {
  if (!slideIds.length) return localTemplate;
  const seen = new Set<string>();
  const uniqueSlideIds = slideIds.filter(slideId => {
    if (seen.has(slideId)) return false;
    seen.add(slideId);
    return true;
  });
  return uniqueSlideIds.reduce(
    (nextTemplate, slideId) => deleteSlideHelper(nextTemplate, slideId),
    localTemplate,
  );
}

const createCurifyPlaceholderSlides = (
  promptInput: PromptInput,
  slides: Slide[],
): Slide[] => {
  const slidesLength = promptInput.constraints?.step;
  const normalSlides = slides.filter(slide => !slide.metadata?.isCurifyPlaceholder);
  const newPlaceholderSlides = Array.from(Array(slidesLength)).map(_ => {
    const newSlide = createNewEmptySlide({
      contentType: 'fullscreen_media',
      fullScreenEmpty: true,
      type: 'slide',
      backgroundMedia: { fileId: '#000000', fileMimeType: 'image/color' },
    });
    newSlide.metadata = { isCurifyPlaceholder: true };
    return newSlide;
  });
  return [...normalSlides, ...newPlaceholderSlides];
};

// Helper to get the deselected UI state
function deselectedUIState(ui: DesignerUIState): DesignerUIState {
  return {
    ...ui,
    selectedTargetElements: [],
    textElementIdBeingEdited: undefined,
    mcExplanationEditingOptionId: undefined,
    editClickPosition: undefined,
    elementTransformationProperties: undefined,
    canInteractWithIframe: false,
    previewAttachmentPassageElementId: undefined,
    anElementIsBeingTransformed: false,
    selectedElements: null,
    richTextEditor: null,
  };
}

// Helper to get selected slide from template state
function getSelectedSlide(template: TemplateState): Slide | undefined {
  if (!template?.content.slides?.length || !template.currentRound) return undefined;
  return template.content.slides.find(r => r.id === template.currentRound);
}

function getSlidesVisibleInSlideList(slides: Slide[]): Slide[] {
  return slides.filter(
    slide =>
      slide.type !== 'voting' && slide.type !== 'podium' && slide.type !== 'responsegrid',
  );
}

function pruneMultiSelectedSlides(
  ui: DesignerUIState,
  template: TemplateState,
): DesignerUIState {
  if (!ui.multiSelectedSlides.length) return ui;
  if (!template?.content.slides?.length) return { ...ui, multiSelectedSlides: [] };
  const visibleSlideIds = new Set(
    getSlidesVisibleInSlideList(template.content.slides).map(s => s.id),
  );
  const next = ui.multiSelectedSlides.filter(
    id => id !== template.currentRound && visibleSlideIds.has(id),
  );
  return next.length === ui.multiSelectedSlides.length &&
    next.every((id, index) => id === ui.multiSelectedSlides[index])
    ? ui
    : { ...ui, multiSelectedSlides: next };
}

function getNextMultiSelectedSlides({
  template,
  currentMultiSelectedSlides,
  clickedSlideId,
  modifiers,
}: {
  template: Template;
  currentMultiSelectedSlides: string[];
  clickedSlideId: string;
  modifiers?: { shiftKey?: boolean; metaKey?: boolean; ctrlKey?: boolean };
}): string[] {
  const visibleSlides = getSlidesVisibleInSlideList(template.content.slides || []);
  const currentRound = template.currentRound;
  const targetIndex = visibleSlides.findIndex(slide => slide.id === clickedSlideId);
  if (targetIndex < 0) return currentMultiSelectedSlides;
  const shouldRangeSelect = !!modifiers?.shiftKey;
  const shouldToggle = !!(modifiers?.metaKey || modifiers?.ctrlKey);

  if (!currentRound || (!shouldRangeSelect && !shouldToggle)) {
    return [];
  }

  if (shouldRangeSelect) {
    const currentIndex = visibleSlides.findIndex(slide => slide.id === currentRound);
    if (currentIndex < 0) return [];
    const start = Math.min(currentIndex, targetIndex);
    const end = Math.max(currentIndex, targetIndex);
    return visibleSlides
      .slice(start, end + 1)
      .map(slide => slide.id)
      .filter(id => id !== currentRound);
  }

  if (clickedSlideId === currentRound) return currentMultiSelectedSlides;
  if (currentMultiSelectedSlides.includes(clickedSlideId)) {
    return currentMultiSelectedSlides.filter(id => id !== clickedSlideId);
  }
  return [...currentMultiSelectedSlides, clickedSlideId];
}

// ─── UI Sub-Reducer ──────────────────────────────────────────────────────────

function uiReducer(state: DesignerUIState, action: DesignerAction): DesignerUIState {
  switch (action.type) {
    case DesignerActions.setIsDesktop: {
      if (state.isDesktop === action.payload) return state;
      if (state.selectedTab.tab === 'none' && action.payload) {
        return {
          ...state,
          selectedTab: { tab: 'addNewSlideOrActivity' },
          isDesktop: action.payload,
          isDrawerOpen: action.payload,
        };
      }
      return { ...state, isDesktop: action.payload, isDrawerOpen: action.payload };
    }
    case DesignerActions.setForceLoadingState:
      return { ...state, forceLoadingState: action.payload };
    case DesignerActions.setLeftTabCollapsed:
      return { ...state, isLeftTabCollapsed: action.isCollapsed };
    case DesignerActions.setSlideListCollapsed:
      return { ...state, isSlideListCollapsed: action.isCollapsed };
    case DesignerActions.setShowTabPaneFullWidth:
      return { ...state, showTabPaneFullWidth: action.isExpanded };
    case DesignerActions.setSelectedPromptId:
      return { ...state, selectedPromptId: action.selectedPromptId };
    case DesignerActions.clearMultiSelectedSlides:
      if (!state.multiSelectedSlides.length) return state;
      return { ...state, multiSelectedSlides: [] };
    case DesignerActions.setMultiSelectedSlides:
      return { ...state, multiSelectedSlides: action.slideIds };
    case DesignerActions.setIsUserIdle:
      return { ...state, isUserIdle: action.payload };
    case DesignerActions.setSelectedTab: {
      const previousTab = state.selectedTab.tab;
      if (action.payload.tab === 'none' && state.isDesktop) {
        return { ...state, selectedTab: { tab: 'addNewSlideOrActivity', previousTab } };
      }
      return {
        ...state,
        selectedTab: { ...action.payload, previousTab },
        isDrawerOpen: !state.isDesktop ? true : state.isDrawerOpen,
        isLeftTabCollapsed: false,
        showTabPaneFullWidth:
          action.payload.tab === 'testCases' ||
          action.payload.tab === 'testCasesResult' ||
          action.payload.tab === 'editAiFeedback' ||
          action.payload.tab === 'settings',
      };
    }
    case DesignerActions.setSelectedTabGoBack: {
      const currentTab = state.selectedTab.tab;
      const previousTab = state.selectedTab.previousTab;
      const isFullWidthTab = (tab: string) =>
        tab === 'testCases' ||
        tab === 'testCasesResult' ||
        tab === 'editAiFeedback' ||
        tab === 'settings';
      if (!previousTab || previousTab === 'none') {
        return {
          ...state,
          selectedTab: { tab: 'addActivity', previousTab: currentTab },
          showTabPaneFullWidth: false,
        };
      }
      return {
        ...state,
        selectedTab: { tab: previousTab, previousTab: currentTab },
        showTabPaneFullWidth: isFullWidthTab(previousTab),
      };
    }
    case DesignerActions.clearDroppedImageData:
      return {
        ...state,
        selectedTab: {
          ...state.selectedTab,
          initialData: { droppedImageData: undefined },
        },
      };
    case DesignerActions.toggleMediaModal:
      return { ...state, showMediaModal: !state.showMediaModal };
    case DesignerActions.closeDrawer:
      return { ...state, isDrawerOpen: false };
    case DesignerActions.selectNewSlide:
      return { ...state, selectedTab: { tab: 'addNewSlideOrActivity' } };
    case DesignerActions.selectEditMedia:
      return {
        ...state,
        selectedTab: { tab: 'media', initialData: { droppedImageData: action.payload } },
        isDrawerOpen: state.isDesktop ? false : true,
      };
    case DesignerActions.updatePromptDependentInputs: {
      const { id, value, type } = action.payload;
      return {
        ...state,
        promptDependentInputs: {
          ...state.promptDependentInputs,
          [id]: { value, id, type },
        },
      };
    }
    case DesignerActions.updatePromptDependentInputsBulk: {
      const newTestCaseData = action.payload.reduce(
        (acc, cur) => ({ ...acc, [cur.id]: cur }),
        state.promptDependentInputs,
      );
      return { ...state, promptDependentInputs: newTestCaseData };
    }
    case DesignerActions.changeTextElementBeingEdited:
      return {
        ...state,
        textElementIdBeingEdited: action.elementId,
        editClickPosition: undefined,
      };
    case DesignerActions.startEditingTextElement:
      if (action.selectAll && state.richTextEditor) {
        selectAll(state.richTextEditor);
      }
      return {
        ...state,
        textElementIdBeingEdited: action.elementId,
        editClickPosition: action.clickPosition
          ? { ...action.clickPosition, ts: Date.now() }
          : undefined,
      };
    case DesignerActions.stopEditingTextElement:
      return { ...state, textElementIdBeingEdited: undefined };
    case DesignerActions.openMcExplanationEditor:
      return { ...state, mcExplanationEditingOptionId: action.optionId };
    case DesignerActions.closeMcExplanationEditor:
      return { ...state, mcExplanationEditingOptionId: undefined };
    case DesignerActions.transformElements: {
      const newElementTransform = action.elementTransformationProperties.map(property => {
        const existingValue = state.elementTransformationProperties?.find(
          e => e.id === property.id,
        );
        return {
          id: property.id,
          transformX: property.transformX,
          transformY: property.transformY,
          transformWidth: property.transformWidth,
          transformHeight: property.transformHeight,
          transformAngle: property.transformAngle,
          transformScale: property.transformScale
            ? property.transformScale
            : existingValue?.transformScale,
        };
      });
      return {
        ...state,
        elementTransformationProperties: newElementTransform,
        anElementIsBeingTransformed: action.shouldSetTransformingElement,
      };
    }
    case DesignerActions.stopTransformingElement:
      return {
        ...state,
        elementTransformationProperties: undefined,
        anElementIsBeingTransformed: false,
      };
    case DesignerActions.selectTargetElements:
      return { ...state, selectedTargetElements: action.selectedTargetElements };
    case DesignerActions.deselectElements:
      return deselectedUIState(state);
    case DesignerActions.setZoom: {
      if (
        action.zoomAction === 'fit' &&
        action.containerFitHeight &&
        action.containerFitWidth
      ) {
        const defaultToolbarHeight = state.isDesktop ? 50 : 80;
        const { containerFitWidth, infiniteViewer } = action;
        const containerFitHeight = action.containerFitHeight - defaultToolbarHeight;
        const isViewerReady = isInfiniteViewerReady(infiniteViewer);
        const aspectRatio = containerFitWidth / containerFitHeight;
        const slideAspectRatio = 1980 / 1080;
        let newZoom = 1;
        if (aspectRatio > slideAspectRatio) {
          newZoom = containerFitHeight / 1080;
          if (infiniteViewer && isViewerReady) {
            const toolbarHeight = defaultToolbarHeight / newZoom;
            newZoom = containerFitHeight / (1080 + toolbarHeight);
            const slideHeight = 1080;
            const deltaHeight = containerFitHeight / newZoom - slideHeight;
            const deltaWidth = containerFitWidth / newZoom - 1980;
            try {
              infiniteViewer.setZoom(newZoom);
              infiniteViewer.scrollTo(-deltaWidth / 2, -toolbarHeight - deltaHeight / 2);
            } catch {
              /* InfiniteViewer not fully initialized */
            }
          }
        } else {
          newZoom = containerFitWidth / 1980;
          if (infiniteViewer && isViewerReady) {
            const toolbarHeight = defaultToolbarHeight / newZoom;
            const deltaWidth = containerFitWidth / newZoom - 1980;
            const deltaHeight = containerFitHeight / newZoom - 1080;
            try {
              infiniteViewer.setZoom(newZoom);
              infiniteViewer.scrollTo(
                -deltaWidth / 2 - 25,
                -toolbarHeight - deltaHeight / 2,
              );
            } catch {
              /* InfiniteViewer not fully initialized */
            }
          }
        }
        return { ...state, zoom: newZoom };
      }
      if (action.zoomAction === 'increase')
        return { ...state, zoom: Math.min(state.zoom + 0.1, MAX_ZOOM) };
      if (action.zoomAction === 'decrease')
        return { ...state, zoom: Math.max(state.zoom - 0.1, MIN_ZOOM) };
      if (action.zoom !== undefined) return { ...state, zoom: action.zoom };
      return state;
    }
    case DesignerActions.allowInteractionWithIframe:
      return { ...state, canInteractWithIframe: true };
    case DesignerActions.previewAttachmentPassage:
      return { ...state, previewAttachmentPassageElementId: action.elementId };
    case DesignerActions.closeAttachmentPassagePreview:
      return { ...state, previewAttachmentPassageElementId: undefined };
    case DesignerActions.disableArrowKeyNavigation:
      return { ...state, isArrowKeyNavigationDisabled: true };
    case DesignerActions.enableArrowKeyNavigation:
      return { ...state, isArrowKeyNavigationDisabled: false };
    case DesignerActions.setTestcaseResults:
      return { ...state, testcaseResults: action.payload };
    case DesignerActions.setSelectedTestcaseResult:
      return { ...state, selectedTestcaseResult: action.payload };

    // selectElements: change which elements are selected (creates slate editors for text)
    case DesignerActions.selectElements: {
      const { selectedElements } = action;
      // Preserve edit mode if re-selecting the same single text element
      const isReselectingSameEditingElement =
        selectedElements?.length === 1 &&
        state.textElementIdBeingEdited === selectedElements[0].id;
      const baseResetState: Partial<DesignerUIState> = {
        textElementIdBeingEdited: isReselectingSameEditingElement
          ? state.textElementIdBeingEdited
          : undefined,
        elementTransformationProperties: undefined,
        selectedTargetElements: selectedElements
          ? selectedElements.map(e => `.target${e?.id}`)
          : [],
      };

      if (!selectedElements || selectedElements.length === 0) {
        return {
          ...state,
          ...baseResetState,
          selectedElements: null,
          richTextEditor: null,
        };
      }

      // Single text element
      if (selectedElements.length === 1 && isSlideElementText(selectedElements[0])) {
        const selectedElement = selectedElements[0];
        if (
          !state.richTextEditor ||
          state.richTextEditor.id !== `${RICHTEXTEDITOR_ID_PREFIX}${selectedElement.id}`
        ) {
          const editor = withMathElements(withReact(createEditor()));
          editor.id = `${RICHTEXTEDITOR_ID_PREFIX}${selectedElement.id}`;
          const nodes = parseStringToSlateObject(selectedElement.textValue);
          Transforms.insertNodes(editor, nodes);
          // Collapse selection to start — avoids a flash of all-selected text
          // when the editor enters edit mode. The caret will be repositioned
          // to the click location by SlateRichTextEditor's useLayoutEffect.
          Transforms.collapse(editor, { edge: 'start' });
          return {
            ...state,
            ...baseResetState,
            selectedElements,
            selectedRichTextEditors: [editor],
            richTextEditor: editor,
          };
        } else {
          return {
            ...state,
            ...baseResetState,
            selectedElements,
          };
        }
      }

      // Single attachment element
      if (
        selectedElements.length === 1 &&
        isSlideElementAttachmentPassage(selectedElements[0])
      ) {
        return {
          ...state,
          ...baseResetState,
          selectedElements,
          richTextEditor: null,
          isLeftTabCollapsed: false,
          selectedTab: {
            previousTab: state.selectedTab.tab,
            tab: 'attachments',
            subTab: 'editAttachment',
            dataState: {
              attachmentTab: { attachmentId: selectedElements[0].attachmentId },
            },
          },
        };
      }

      // Multiple elements
      const newSelectedElements = selectedElements.length > 0 ? selectedElements : null;
      const selectedTextElements = newSelectedElements?.filter(
        e => e.type === 'text' || e.type === 'generatorText',
      );
      if (selectedTextElements && selectedTextElements.length > 0) {
        const newEditors = selectedTextElements.map(e => {
          const editor = withMathElements(withReact(createEditor()));
          editor.id = `${RICHTEXTEDITOR_ID_PREFIX}${e.id}`;
          const nodes = parseStringToSlateObject(e.textValue);
          Transforms.insertNodes(editor, nodes);
          selectAll(editor);
          return editor;
        });
        return {
          ...state,
          ...baseResetState,
          selectedElements: newSelectedElements,
          richTextEditor: null,
          selectedRichTextEditors: newEditors,
        };
      }
      return {
        ...state,
        ...baseResetState,
        selectedElements: newSelectedElements,
        richTextEditor: null,
      };
    }

    // updateSelectedElements: update properties of already-selected elements without resetting editors
    case DesignerActions.updateSelectedElements:
      return { ...state, selectedElements: action.selectedElements };

    case DesignerActions.setAITextElementId:
      return {
        ...state,
        aiConnection: {
          ...state.aiConnection,
          aiTextElementId: action.elementId,
          isPollOption: action.isPollOption,
        },
      };
    case DesignerActions.setAIImageElementId:
      return {
        ...state,
        aiConnection: { ...state.aiConnection, aiImageElementId: action.elementId },
      };
    case DesignerActions.clearAiElements:
      return {
        ...state,
        aiConnection: {
          ...state.aiConnection,
          aiImageElementId: undefined,
          aiTextElementId: undefined,
        },
      };
    case DesignerActions.setAITextPromptSourceId:
      return {
        ...state,
        aiConnection: { ...state.aiConnection, promptSourceId: action.promptSourceId },
      };
    case DesignerActions.setAIImagePromptSourceId:
      return {
        ...state,
        aiConnection: {
          ...state.aiConnection,
          promptImageSourceId: action.promptImageSourceId,
        },
      };
    case DesignerActions.resetAIConnection:
      return { ...state, aiConnection: undefined };
    case DesignerActions.showTemplateAccessErrorModal:
      return {
        ...state,
        templateAccessErrorModal: { isVisible: true, errorType: action.errorType },
      };
    case DesignerActions.showCopyLessonModal:
      return {
        ...state,
        copyLessonModal: {
          isVisible: true,
          templateMetadata: action.payload?.templateMetadata,
          stampMetadata: action.payload?.stampMetadata,
        },
      };
    case DesignerActions.setIsAutoCopyingLesson:
      return { ...state, isAutoCopyingLesson: action.payload };
    case DesignerActions.hideCopyLessonModal:
      return { ...state, copyLessonModal: { isVisible: false } };
    case DesignerActions.showPresentOrPreviewModal:
      return { ...state, isPresentOrPreviewModalVisible: true };
    case DesignerActions.hidePresentOrPreviewModal:
      return { ...state, isPresentOrPreviewModalVisible: false };
    case DesignerActions.showVariantPlaceholderLinkModal:
      return { ...state, isVariantPlaceholderLinkModalVisible: true };
    case DesignerActions.hideVariantPlaceholderLinkModal:
      return { ...state, isVariantPlaceholderLinkModalVisible: false };
    case DesignerActions.setHoveredRetrySourceSlideId:
      return { ...state, hoveredRetrySourceSlideId: action.slideId };
    case DesignerActions.agentStreamStarted:
      return {
        ...state,
        agent: {
          ...state.agent,
          isStreaming: true,
          isComplete: false,
          state: 'thinking',
          error: null,
        },
      };
    case DesignerActions.agentReset:
      return {
        ...state,
        agent: {
          ...state.agent,
          isStreaming: false,
          isComplete: false,
          state: null,
          error: null,
        },
      };
    case DesignerActions.agentStreamError:
      return {
        ...state,
        agent: { ...state.agent, error: action.payload, isStreaming: false, state: null },
        initialLessonBuilding: undefined,
      };
    case DesignerActions.agentStreamOnStateChange:
      return { ...state, agent: { ...state.agent, state: action.payload.state } };
    case DesignerActions.agentStreamOnComplete:
      return {
        ...state,
        agent: {
          ...state.agent,
          state: null,
          isComplete: action.payload.isComplete,
          isStreaming: false,
        },
      };
    case DesignerActions.agentStreamStopped:
      return {
        ...state,
        agent: {
          ...state.agent,
          error: null,
          isComplete: false,
          isStreaming: false,
          state: null,
        },
        initialLessonBuilding: undefined,
      };
    case DesignerActions.startInitialLessonBuilding:
      if (!state.initialLessonBuilding) return state;
      return {
        ...state,
        initialLessonBuilding: {
          ...state.initialLessonBuilding,
          isLoading: true,
          started: true,
        },
      };
    case DesignerActions.completeInitialLessonBuilding:
      if (!state.initialLessonBuilding) return state;
      return { ...state, initialLessonBuilding: undefined };
    case DesignerActions.initLoaderState: {
      const { gameId } = action.payload;
      if (state.loaderStates[gameId]) return state;
      return {
        ...state,
        loaderStates: { ...state.loaderStates, [gameId]: { startTime: Date.now() } },
      };
    }
    default:
      return state;
  }
}

// ─── Template Sub-Reducer ────────────────────────────────────────────────────

function templateReducer(state: TemplateState, action: DesignerAction): TemplateState {
  switch (action.type) {
    case DesignerActions.initTemplate: {
      return action.template;
    }
    case DesignerActions.changeSelectedSlide: {
      if (!state) return state;
      const exists = state.content.slides?.some(s => s.id === action.slideId);
      if (!exists) return state;
      return { ...state, currentRound: action.slideId };
    }
    case DesignerActions.deleteSlide: {
      if (!state) return state;
      return deleteSlideHelper(state, action.slideId);
    }
    case DesignerActions.deleteSlides: {
      if (!state) return state;
      return deleteSlidesHelper(state, action.slideIds);
    }
    case DesignerActions.initActiveGame: {
      if (!state) return state;
      return {
        ...state,
        content: { ...state.content, activeGame: action.activeGame },
      };
    }
    case DesignerActions.initSetFirstSlide: {
      if (!state) return state;
      return { ...state, currentRound: state.content?.slides?.[0]?.id };
    }
    case DesignerActions.nextSlide: {
      if (!state) return state;
      return nextSlideHelper(state);
    }
    case DesignerActions.previousSlide: {
      if (!state) return state;
      return previousSlideHelper(state);
    }
    case DesignerActions.copySlide: {
      if (!state) return state;
      return duplicateSlideHelper(state, action.slideId);
    }
    case DesignerActions.endGame: {
      if (!state) return state;
      return { ...state, content: { ...state.content, activeGame: null } };
    }
    case DesignerActions.changeSlide: {
      if (!state) return state;
      const slides = [...(state.content.slides || [])];
      const newSlides = slides.map(s => (s.id === action.slide.id ? action.slide : s));
      return { ...state, content: { ...state.content, slides: newSlides } };
    }
    case DesignerActions.addSlide: {
      if (!state) return state;
      if (!action.slides?.length) return state;
      const selectedSlide = state.content.slides?.find(s => s.id === state.currentRound);
      const existingSlides = [...(state.content.slides || [])];
      let slides = insertSlidesAfterSelectedSlide(
        existingSlides,
        action.slides,
        selectedSlide?.id,
      );
      if (action.replace && selectedSlide) {
        const relatedSlides = findRelatedSlidesWithoutIndex(selectedSlide.id, slides).map(
          s => s.id,
        );
        slides = slides.filter(s => !relatedSlides.includes(s.id));
      }
      return {
        ...state,
        currentRound: action.slides[0].id,
        content: { ...state.content, slides },
      };
    }
    case DesignerActions.addTranslatedSlides: {
      if (!state) return state;
      if (!action.slides?.length) return state;
      const slidesToBeTranslated = action.roundsToTranslate;
      const existingSlides = [...(state.content.slides || [])];
      const newTranslatedSlides = action.slides;
      let newSelectedSlide: string | undefined;
      const relatedSlides: Record<string, Slide[]> = {};
      slidesToBeTranslated.forEach((slide, i) => {
        relatedSlides[slide.slideId] = makeNewIdsForSlide(
          findRelatedSlidesWithoutIndex(slide.slideId, newTranslatedSlides).map(s => s),
        );
        if (i === 0) newSelectedSlide = relatedSlides[slide.slideId][0].id;
      });

      let slides: Slide[] = [];
      slidesToBeTranslated.forEach(slide => {
        const currentRelatedSlides = findRelatedSlidesWithoutIndex(
          slide.slideId,
          existingSlides,
        );
        const firstRelatedSlide = currentRelatedSlides[0];
        const firstRelatedSlideId = existingSlides.find(
          s => s.id === firstRelatedSlide.id,
        )?.id;
        if (!firstRelatedSlideId) return;
        slides = insertSlidesBeforeSelectedSlide(
          slides.length === 0 ? existingSlides : slides,
          relatedSlides[slide.slideId],
          firstRelatedSlideId,
        );
      });

      if (
        state.attachments &&
        action.lessonAttachments &&
        action.lessonAttachments.length > 0
      ) {
        return {
          ...state,
          currentRound: newSelectedSlide,
          content: { ...state.content, slides },
          attachments: [...state.attachments, ...action.lessonAttachments],
        };
      }
      return {
        ...state,
        currentRound: newSelectedSlide,
        content: { ...state.content, slides },
      };
    }
    case DesignerActions.allSlidesChanged: {
      if (!state) return state;
      return {
        ...state,
        content: { ...state.content, slides: action.slides },
        currentRound: action.selectedSlideId,
      };
    }
    case DesignerActions.agentUpdatedState: {
      if (!state) return state;
      let currentRound = action.keepCurrentSlide
        ? state.currentRound
        : action.selectedSlideId;

      if (action.keepCurrentSlide && !state.currentRound && action.slides.length > 0) {
        const slideGroups = createSlideGroups(action.slides);
        const firstSlide = slideGroups[0][0];
        currentRound = firstSlide.id;
      }

      const currentLoadingSlide = state.content.slides?.find(
        s => s.id === LOADING_SLIDE_ID,
      );
      const hasLoadingSlideInNewSlides = action.slides.some(
        s => s.id === LOADING_SLIDE_ID,
      );
      const shouldPreserveLoadingSlide =
        currentLoadingSlide && !hasLoadingSlideInNewSlides;
      const mergedSlides = shouldPreserveLoadingSlide
        ? [currentLoadingSlide, ...action.slides]
        : action.slides;

      return {
        ...state,
        content: { ...state.content, slides: mergedSlides },
        currentRound,
        attachments: action.attachments,
        metadata: action.metadata,
      };
    }
    case DesignerActions.setBackgroundOnAllSlides: {
      if (!state) return state;
      return {
        ...state,
        content: {
          ...state.content,
          slides: (state.content?.slides || []).map(r => ({
            ...r,
            backgroundMedia: action.media,
          })),
        },
      };
    }
    case DesignerActions.titleChanged: {
      if (!state) return state;
      return { ...state, title: action.value };
    }
    case DesignerActions.descriptionChanged: {
      if (!state) return state;
      return { ...state, description: action.value };
    }
    case DesignerActions.coverImageAltTextChanged: {
      if (!state) return state;
      return { ...state, coverImageAltText: action.value };
    }
    case DesignerActions.languageChanged: {
      if (!state) return state;
      return { ...state, language: action.language };
    }
    case DesignerActions.parentIdChanged: {
      if (!state) return state;
      return { ...state, parentId: action.parentId, isPrivate: action.isPrivate };
    }
    case DesignerActions.contentFilteringChanged: {
      if (!state) return state;
      return { ...state, isContentFilterEnabled: action.isEnabled };
    }
    case DesignerActions.realNamesChanged: {
      if (!state) return state;
      return { ...state, requireRealNames: action.require };
    }
    case DesignerActions.coverPhotoChanged: {
      if (!state) return state;
      return {
        ...state,
        coverImageId: action.coverImageId,
        coverImageAltText: action.coverImageAltText,
      };
    }
    case DesignerActions.sharingPublicChanged: {
      if (!state) return state;
      return { ...state, templateSharingPublic: action.templateSharingPublic };
    }
    case DesignerActions.metadataPropertyChanged: {
      if (!state) return state;
      return {
        ...state,
        metadata: { ...state.metadata, [action.payload.key]: action.payload.value },
      };
    }
    case DesignerActions.settingsPropertyChanged: {
      if (!state) return state;
      return {
        ...state,
        settings: { ...state.settings, [action.payload.key]: action.payload.value },
      };
    }
    case DesignerActions.isBlockedFromSharingChanged: {
      if (!state) return state;
      return { ...state, isBlockedFromSharing: action.isBlockedFromSharing };
    }
    case DesignerActions.isPrivateChanged: {
      if (!state) return state;
      return { ...state, isPrivate: action.isPrivate };
    }
    case DesignerActions.isPremiumContentChanged: {
      if (!state) return state;
      return {
        ...state,
        content: {
          ...state.content,
          slides: stampSlidesPremiumContent(
            state.content.slides,
            action.isPremiumContent,
          ),
        },
      };
    }
    case DesignerActions.addPrompt: {
      if (!state) return state;
      return {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: [
            ...(state.aiContent?.prompts || []),
            createNewPrompt(
              action.newPromptId,
              action.promptName,
              action.newPromptTemplate,
            ),
          ],
        },
      };
    }
    case DesignerActions.addImagePrompt: {
      if (!state) return state;
      return {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: [
            ...(state.aiContent?.prompts || []),
            createNewImagePrompt(
              action.newPromptId,
              action.promptName,
              action.newPromptTemplate,
            ),
          ],
        },
      };
    }
    case DesignerActions.deletePrompt: {
      if (!state) return state;
      return {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: state.aiContent?.prompts?.filter(p => p.id !== action.promptId) || [],
        },
      };
    }
    case DesignerActions.duplicatePrompt: {
      if (!state) return state;
      const prompt = state.aiContent?.prompts?.find(p => p.id === action.promptId);
      if (!prompt) return state;
      return {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: [
            ...(state.aiContent?.prompts || []),
            { ...prompt, id: createPromptUniqueId(), name: `${prompt.name} (copy)` },
          ],
        },
      };
    }
    case DesignerActions.editPrompt: {
      if (!state) return state;
      return {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: (state.aiContent?.prompts || []).map(eachPrompt => {
            if (eachPrompt.id === action.prompt.id) {
              return action.prompt;
            }
            return updatePromptOutputElementLabelById({
              prompt: eachPrompt,
              id: action.prompt.id,
              updatedLabel: action.prompt.name,
            });
          }),
        },
      };
    }
    case DesignerActions.addPromptInput: {
      if (!state) return state;
      const newPromptInput = action.promptInput || {
        id: `input-${generateUniqueId()}`,
        label: 'New input',
        order: state.aiContent?.promptInputs?.length || 0,
        type: 'shortText',
        allowEmpty: false,
        isRequired: true,
      };
      return {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          promptInputs: [...(state.aiContent?.promptInputs || []), newPromptInput],
        },
      };
    }
    case DesignerActions.deletePromptInput: {
      if (!state) return state;
      return {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          promptInputs: (state.aiContent?.promptInputs || []).filter(
            input => input.id !== action.inputId,
          ),
        },
      };
    }
    case DesignerActions.editPromptInput: {
      if (!state) return state;
      return {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          promptInputs: (state.aiContent?.promptInputs || []).map(input =>
            input.id === action.promptInput.id ? action.promptInput : input,
          ),
          prompts: (state.aiContent?.prompts || []).map(eachPrompt =>
            updatePromptInputElementLabelById({
              prompt: eachPrompt,
              id: action.promptInput.id,
              updatedLabel: action.promptInput.label,
            }),
          ),
        },
      };
    }
    case DesignerActions.changePromptMessageRole: {
      if (!state) return state;
      return {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: (state.aiContent?.prompts || []).map(p => ({
            ...p,
            messages: p.messages.map((m, i) =>
              i === action.messageIndex ? { ...m, role: action.role } : m,
            ),
          })),
        },
      };
    }
    case DesignerActions.changePromptOutputType: {
      if (!state) return state;
      const newId = `output-${generateUniqueId()}`;
      const newOutputProperty: ById<PromptOutputProperty> = {};
      if (action.outputType === 'object') {
        newOutputProperty[newId] = { description: '', id: newId, type: 'string' };
      }
      return {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: (state.aiContent?.prompts || []).map(p =>
            p.id === action.promptId
              ? {
                  ...p,
                  promptOutput: {
                    ...p.promptOutput,
                    type: action.outputType,
                    properties: newOutputProperty,
                    propertiesOrder: action.outputType !== 'string' ? [newId] : undefined,
                  },
                }
              : p,
          ),
        },
      };
    }
    case DesignerActions.addPromptOutputProperty: {
      if (!state) return state;
      const propertyId = `output-${generateUniqueId()}`;
      return {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: (state.aiContent?.prompts || []).map(p => {
            if (p.id === action.promptId) {
              const currentPropertiesOrder = p.promptOutput.propertiesOrder || [];
              const propertiesOrder = currentPropertiesOrder.length
                ? currentPropertiesOrder
                : Object.keys(p.promptOutput.properties).reduce(
                    (acc, key) => [...acc, key],
                    [] as string[],
                  );
              return {
                ...p,
                promptOutput: {
                  ...p.promptOutput,
                  properties: {
                    ...p.promptOutput.properties,
                    [propertyId]: { id: propertyId, type: 'string', description: '' },
                  },
                  propertiesOrder: [...propertiesOrder, propertyId],
                },
              };
            }
            return p;
          }),
        },
      };
    }
    case DesignerActions.editPromptOutputProperty: {
      if (!state) return state;
      return {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: (state.aiContent?.prompts || []).map(p => {
            if (p.id === action.promptId) {
              const currentPropertiesOrder = p.promptOutput.propertiesOrder || [];
              const propertiesOrder = action.promptOutputPropertyOrder
                ? action.promptOutputPropertyOrder
                : currentPropertiesOrder.length
                ? currentPropertiesOrder
                : Object.keys(p.promptOutput.properties).reduce(
                    (acc, key) => [...acc, key],
                    [] as string[],
                  );
              return {
                ...p,
                promptOutput: {
                  ...p.promptOutput,
                  properties: {
                    ...p.promptOutput.properties,
                    [action.promptOutputProperty.id]: action.promptOutputProperty,
                  },
                  propertiesOrder,
                },
              };
            }
            return p;
          }),
        },
      };
    }
    case DesignerActions.deletePromptOutputProperty: {
      if (!state) return state;
      const promptForDeletion = (state.aiContent?.prompts || []).find(
        p => p.id === action.promptId,
      );
      if (!promptForDeletion) throw new Error('No prompt found');
      const promptOutput = { ...promptForDeletion.promptOutput };
      delete promptOutput.properties[action.promptOutputPropertyId];
      return {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: (state.aiContent?.prompts || []).map(p => {
            if (p.id === action.promptId) {
              const propertiesOrder = promptOutput.propertiesOrder?.filter(
                id => id !== action.promptOutputPropertyId,
              );
              return {
                ...p,
                promptOutput: {
                  ...p.promptOutput,
                  properties: { ...promptOutput.properties },
                  propertiesOrder: propertiesOrder?.length ? propertiesOrder : undefined,
                },
              };
            }
            return p;
          }),
        },
      };
    }
    case DesignerActions.editGeneratorCategories: {
      if (!state) return state;
      return {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          categories: action.categories,
        },
      };
    }
    case DesignerActions.convertTextElementToAITextElement: {
      if (!state) return state;
      const currentSlide = state.content.slides?.find(
        r => r.id === state.currentRound,
      ) as Slide;
      if (
        (currentSlide.type === 'poll' || currentSlide.type === 'multipleChoice') &&
        action.isPollOption
      ) {
        const pollOptions = currentSlide.pollOptions?.map(pollOption => {
          if (pollOption.id === action.elementId) {
            const blocks = parseStringToSlateObject(pollOption.displayName);
            const firstParagraph = blocks.find(b => b.type === 'paragraph');
            const textChild = firstParagraph?.children?.[0];
            const textColor = textChild
              ? isCuripodText(textChild)
                ? textChild.color
                : firstParagraph?.color || theme.colors.black
              : firstParagraph?.color || theme.colors.black;
            const newParagraph = createSimpleSlateParagraphFromAIOutput({
              text: action.exampleText,
              align: firstParagraph?.align || 'left',
              color: textColor,
              size: firstParagraph?.size || 40,
            });
            return {
              ...pollOption,
              displayName: JSON.stringify([newParagraph]),
              promptSourceId: action.promptSourceId,
            };
          }
          return pollOption;
        });
        currentSlide.pollOptions = pollOptions;
      }
      const slideElements: SlideElement[] = currentSlide.elements.map(e => {
        if (e.id === action.elementId) {
          const blocks = parseStringToSlateObject(e.textValue);
          const firstParagraph = blocks.find(b => b.type === 'paragraph');
          const textChild = firstParagraph?.children?.[0];
          const textColor = textChild
            ? isCuripodText(textChild)
              ? textChild.color
              : firstParagraph?.color || theme.colors.black
            : firstParagraph?.color || theme.colors.black;
          const newParagraph = createSimpleSlateParagraphFromAIOutput({
            text: action.exampleText,
            align: firstParagraph?.align || 'left',
            color: textColor,
            size: firstParagraph?.size || 40,
          });
          return {
            ...e,
            type: 'generatorText',
            promptSourceId: action.promptSourceId,
            textValue: JSON.stringify([newParagraph]),
          };
        }
        return e;
      });
      currentSlide.elements = slideElements;
      const slides = [...(state.content.slides || [])];
      const newSlides = slides.map(s => (s.id === currentSlide.id ? currentSlide : s));
      return { ...state, content: { ...state.content, slides: newSlides } };
    }
    case DesignerActions.convertImageElementToAIImageElement: {
      if (!state) return state;
      const currentSlide = state.content.slides?.find(
        r => r.id === state.currentRound,
      ) as Slide;
      const slideElements: SlideElement[] = currentSlide.elements.map(e => {
        if (e.id === action.elementId) {
          return {
            ...e,
            type: 'generatorImage',
            promptSourceId: action.promptImageSourceId,
            imageObjectFit: 'cover',
            mediaFileId: action.exampleImage.id,
            mediaMimetype: action.exampleImage.mimetype,
            mediaUrl: action.exampleImage.url,
            mediaThumbnailUrl: action.exampleImage.thumbnail,
            mediaCropConfig: undefined,
            mediaAltText: undefined,
          };
        }
        return e;
      });
      currentSlide.elements = slideElements;
      const slides = [...(state.content.slides || [])];
      const newSlides = slides.map(s => (s.id === currentSlide.id ? currentSlide : s));
      return { ...state, content: { ...state.content, slides: newSlides } };
    }
    case DesignerActions.changeSubjects: {
      if (!state) return state;
      return {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          subjects: action.subjects,
        },
      };
    }
    case DesignerActions.changeGrades: {
      if (!state) return state;
      return {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          grades: action.grades,
        },
      };
    }
    case DesignerActions.addCurifyPlaceholderSlides: {
      if (!state) return state;
      const promptInput = state.aiContent?.promptInputs.find(
        p => p.id === action.promptInputId,
      );
      if (!promptInput) return state;
      const newSlides = createCurifyPlaceholderSlides(
        promptInput,
        state.content.slides || [],
      );
      return { ...state, content: { ...state.content, slides: newSlides } };
    }
    case DesignerActions.attachmentUpserted: {
      if (!state) return state;
      const exists = (state.attachments || []).some(
        a => a.attachmentId === action.attachment.attachmentId,
      );
      const newAttachments = exists
        ? state.attachments
        : [...(state.attachments || []), action.attachment];
      return { ...state, attachments: newAttachments };
    }
    case DesignerActions.attachmentCreated: {
      if (!state) return state;
      return {
        ...state,
        attachments: [...(state.attachments || []), action.attachment],
      };
    }
    case DesignerActions.attachmentEdited: {
      if (!state) return state;
      return {
        ...state,
        attachments: (state.attachments || []).map(a =>
          a.attachmentId === action.attachment.attachmentId ? action.attachment : a,
        ),
      };
    }
    case DesignerActions.attachmentDeleted: {
      if (!state) return state;
      return {
        ...state,
        content: {
          ...state.content,
          slides: (state.content?.slides || []).map(slide => ({
            ...slide,
            elements: slide.elements.filter(e => e.attachmentId !== action.attachmentId),
          })),
        },
        attachments: (state.attachments || []).filter(
          a => a.attachmentId !== action.attachmentId,
        ),
      };
    }
    default:
      return state;
  }
}

// ─── Merged Reducer ──────────────────────────────────────────────────────────

// Actions that only affect UI state
const UI_ONLY_ACTIONS = new Set<string>([
  DesignerActions.setSelectedTab,
  DesignerActions.setSelectedTabGoBack,
  DesignerActions.clearDroppedImageData,
  DesignerActions.setIsDesktop,
  DesignerActions.setLeftTabCollapsed,
  DesignerActions.setSlideListCollapsed,
  DesignerActions.setShowTabPaneFullWidth,
  DesignerActions.toggleMediaModal,
  DesignerActions.closeDrawer,
  DesignerActions.selectNewSlide,
  DesignerActions.selectEditMedia,
  DesignerActions.selectSlide,
  DesignerActions.setForceLoadingState,
  DesignerActions.setIsUserIdle,
  DesignerActions.startEditingTextElement,
  DesignerActions.stopEditingTextElement,
  DesignerActions.openMcExplanationEditor,
  DesignerActions.closeMcExplanationEditor,
  DesignerActions.changeTextElementBeingEdited,
  DesignerActions.transformElements,
  DesignerActions.stopTransformingElement,
  DesignerActions.selectTargetElements,
  DesignerActions.deselectElements,
  DesignerActions.setZoom,
  DesignerActions.clearTextElementHeight,
  DesignerActions.allowInteractionWithIframe,
  DesignerActions.previewAttachmentPassage,
  DesignerActions.closeAttachmentPassagePreview,
  DesignerActions.selectElements,
  DesignerActions.updateSelectedElements,
  DesignerActions.setSelectedPromptId,
  DesignerActions.disableArrowKeyNavigation,
  DesignerActions.setTouchDeviceElementClicked,
  DesignerActions.enableArrowKeyNavigation,
  DesignerActions.setAITextElementId,
  DesignerActions.clearAiElements,
  DesignerActions.setAIImageElementId,
  DesignerActions.setAITextPromptSourceId,
  DesignerActions.setAIImagePromptSourceId,
  DesignerActions.resetAIConnection,
  DesignerActions.showTemplateAccessErrorModal,
  DesignerActions.showCopyLessonModal,
  DesignerActions.setIsAutoCopyingLesson,
  DesignerActions.hideCopyLessonModal,
  DesignerActions.showPresentOrPreviewModal,
  DesignerActions.hidePresentOrPreviewModal,
  DesignerActions.showVariantPlaceholderLinkModal,
  DesignerActions.hideVariantPlaceholderLinkModal,
  DesignerActions.setHoveredRetrySourceSlideId,
  DesignerActions.setTestcaseResults,
  DesignerActions.setSelectedTestcaseResult,
  DesignerActions.updatePromptDependentInputs,
  DesignerActions.updatePromptDependentInputsBulk,
  DesignerActions.agentStreamStarted,
  DesignerActions.agentReset,
  DesignerActions.agentStreamStopped,
  DesignerActions.agentStreamError,
  DesignerActions.agentStreamOnStateChange,
  DesignerActions.agentStreamOnComplete,
  DesignerActions.startInitialLessonBuilding,
  DesignerActions.completeInitialLessonBuilding,
  DesignerActions.initLoaderState,
  DesignerActions.clearMultiSelectedSlides,
  DesignerActions.setMultiSelectedSlides,
]);

// Actions that only affect template state
const TEMPLATE_ONLY_ACTIONS = new Set<string>([
  DesignerActions.initTemplate,
  DesignerActions.changeSelectedSlide,
  DesignerActions.deleteSlide,
  DesignerActions.deleteSlides,
  DesignerActions.initActiveGame,
  DesignerActions.initSetFirstSlide,
  DesignerActions.nextSlide,
  DesignerActions.previousSlide,
  DesignerActions.copySlide,
  DesignerActions.endGame,
  DesignerActions.changeSlide,
  DesignerActions.addSlide,
  DesignerActions.addTranslatedSlides,
  DesignerActions.allSlidesChanged,
  DesignerActions.agentUpdatedState,
  DesignerActions.setBackgroundOnAllSlides,
  DesignerActions.titleChanged,
  DesignerActions.descriptionChanged,
  DesignerActions.coverImageAltTextChanged,
  DesignerActions.languageChanged,
  DesignerActions.parentIdChanged,
  DesignerActions.changeSubjects,
  DesignerActions.changeGrades,
  DesignerActions.realNamesChanged,
  DesignerActions.contentFilteringChanged,
  DesignerActions.coverPhotoChanged,
  DesignerActions.sharingPublicChanged,
  DesignerActions.isBlockedFromSharingChanged,
  DesignerActions.isPrivateChanged,
  DesignerActions.isPremiumContentChanged,
  DesignerActions.addPrompt,
  DesignerActions.addImagePrompt,
  DesignerActions.editPrompt,
  DesignerActions.duplicatePrompt,
  DesignerActions.deletePrompt,
  DesignerActions.addPromptInput,
  DesignerActions.deletePromptInput,
  DesignerActions.editPromptInput,
  DesignerActions.changePromptMessageRole,
  DesignerActions.changePromptOutputType,
  DesignerActions.addPromptOutputProperty,
  DesignerActions.editPromptOutputProperty,
  DesignerActions.deletePromptOutputProperty,
  DesignerActions.convertTextElementToAITextElement,
  DesignerActions.convertImageElementToAIImageElement,
  DesignerActions.editGeneratorCategories,
  DesignerActions.addCurifyPlaceholderSlides,
  DesignerActions.attachmentCreated,
  DesignerActions.attachmentUpserted,
  DesignerActions.attachmentEdited,
  DesignerActions.attachmentDeleted,
  DesignerActions.metadataPropertyChanged,
  DesignerActions.slideVariationDesignation,
  DesignerActions.settingsPropertyChanged,
]);

// Actions that should not update modifiedAt (server-loaded data, not user edits)
const SKIP_MODIFIED_AT_ACTIONS = new Set<string>([
  DesignerActions.initTemplate,
  DesignerActions.initSetFirstSlide,
  DesignerActions.initActiveGame,
  DesignerActions.endGame,
  DesignerActions.agentUpdatedState,
]);

// ─── Undo / Redo sub-reducer ─────────────────────────────────────────────────

const HISTORY_LIMIT = 200;

function snapshotFromState(state: DesignerState): DesignerSnapshot {
  // Exclude non-serializable and transient UI state:
  // - Slate editors: contain methods that can't be serialized (rebuilt on restore)
  // - Transform properties: transient drag/resize state that would cause visual
  //   glitches if restored (element appears at dragged position via CSS offset
  //   even though the template has the old coordinates)

  const serializableUI: DesignerUIState = { ...state.ui };
  serializableUI.richTextEditor = null;
  serializableUI.selectedRichTextEditors = [];
  serializableUI.elementTransformationProperties = undefined;
  serializableUI.anElementIsBeingTransformed = false;

  return JSON.parse(
    JSON.stringify({
      ui: serializableUI,
      template: state.template,
    }),
  );
}

function pushToUndoStack(state: DesignerState): DesignerState {
  const snapshot = snapshotFromState(state);
  const newStack = [...state.history.undoStack, snapshot];
  if (newStack.length > HISTORY_LIMIT) {
    newStack.splice(0, 1);
  }
  return {
    ...state,
    history: { undoStack: newStack, redoStack: [], canUndo: true, canRedo: false },
  };
}

// Prepare snapshot UI for restoration after undo/redo.
// Slate editors are excluded from snapshots (can't be serialized). We do NOT
// rebuild them here — doing so would trigger Slate onChange → elementsChanged
// → push to undo stack → infinite loop. Instead we restore the snapshot as-is
// (with null editors) and stop any active text editing. The editors will be
// recreated naturally when the user clicks into a text element again.
function prepareRestoredUI(snapshotUI: DesignerUIState): DesignerUIState {
  return {
    ...snapshotUI,
    textElementIdBeingEdited: undefined,
    mcExplanationEditingOptionId: undefined,
    editClickPosition: undefined,
    richTextEditor: null,
    selectedRichTextEditors: [],
  };
}

function handleUndoAction(state: DesignerState): DesignerState {
  if (state.history.undoStack.length === 0) return state;

  const newUndoStack = [...state.history.undoStack];
  const snapshot = newUndoStack.pop()!;

  // Push current state onto redo stack
  const currentSnapshot = snapshotFromState(state);
  const newRedoStack = [...state.history.redoStack, currentSnapshot];

  return {
    ...state,
    ui: prepareRestoredUI(snapshot.ui),
    template: snapshot.template
      ? { ...snapshot.template, modifiedAt: new Date().getTime() }
      : snapshot.template,
    history: {
      undoStack: newUndoStack,
      redoStack: newRedoStack,
      canUndo: newUndoStack.length > 0,
      canRedo: true,
    },
  };
}

function handleRedoAction(state: DesignerState): DesignerState {
  if (state.history.redoStack.length === 0) return state;

  const newRedoStack = [...state.history.redoStack];
  const snapshot = newRedoStack.pop()!;

  // Push current state onto undo stack
  const currentSnapshot = snapshotFromState(state);
  const newUndoStack = [...state.history.undoStack, currentSnapshot];

  return {
    ...state,
    ui: prepareRestoredUI(snapshot.ui),
    template: snapshot.template
      ? { ...snapshot.template, modifiedAt: new Date().getTime() }
      : snapshot.template,
    history: {
      undoStack: newUndoStack,
      redoStack: newRedoStack,
      canUndo: true,
      canRedo: newRedoStack.length > 0,
    },
  };
}

export function designerReducer(
  inputState: DesignerState,
  action: DesignerAction,
): DesignerState {
  // ── Undo / Redo ──────────────────────────────────────────────────────
  if (action.type === DesignerActions.undo) return handleUndoAction(inputState);
  if (action.type === DesignerActions.redo) return handleRedoAction(inputState);

  // ── Auto-snapshot before template-mutating actions ───────────────────
  let state = inputState;

  if (
    TEMPLATE_MUTATING_ACTIONS.has(action.type) &&
    !action.skipHistory &&
    !SKIP_HISTORY_ACTIONS.has(action.type) &&
    state.template
  ) {
    // For selectElements, skip the snapshot if the selection hasn't changed
    // (e.g. Selecto and onClick both fire on the same click)
    const isRedundantSelect =
      action.type === DesignerActions.selectElements &&
      action.selectedElements &&
      state.ui.selectedElements &&
      action.selectedElements.length === state.ui.selectedElements.length &&
      action.selectedElements.every(
        (el, i) => el.id === state.ui.selectedElements![i]?.id,
      );
    if (!isRedundantSelect) {
      state = pushToUndoStack(state);
    }
  }

  // UI-only actions
  if (UI_ONLY_ACTIONS.has(action.type)) {
    return { ...state, ui: uiReducer(state.ui, action) };
  }

  // Template-only actions
  if (TEMPLATE_ONLY_ACTIONS.has(action.type)) {
    const newTemplate = templateReducer(state.template, action);
    if (
      newTemplate &&
      newTemplate !== state.template &&
      !SKIP_MODIFIED_AT_ACTIONS.has(action.type)
    ) {
      newTemplate.modifiedAt = new Date().getTime();
    }
    return {
      ...state,
      template: newTemplate,
      ui: pruneMultiSelectedSlides(state.ui, newTemplate),
    };
  }

  // Cross-cutting actions
  switch (action.type) {
    case DesignerActions.slidesDragged: {
      if (!state) return state;
      if (!state.template) return state;

      return {
        ...state,
        template: {
          ...state.template,
          content: { ...state.template?.content, slides: action.slides },
          modifiedAt: new Date().getTime(),
        },
      };
    }
    case DesignerActions.slideSelected: {
      const clearedUI = deselectedUIState(state.ui);
      return {
        ...state,
        ui: { ...clearedUI, multiSelectedSlides: [] },
        template: state.template
          ? {
              ...state.template,
              currentRound: action.slideId,
              modifiedAt: new Date().getTime(),
            }
          : state.template,
      };
    }

    case DesignerActions.slideListSelectionChanged: {
      if (!state.template) return state;
      const slideExists = state.template.content.slides?.some(
        slide => slide.id === action.slideId,
      );
      if (!slideExists) return state;

      const hasModifier = !!(
        action.modifiers?.shiftKey ||
        action.modifiers?.metaKey ||
        action.modifiers?.ctrlKey
      );
      if (!hasModifier) {
        const clearedUI = deselectedUIState(state.ui);
        return {
          ...state,
          ui: { ...clearedUI, multiSelectedSlides: [] },
          template: {
            ...state.template,
            currentRound: action.slideId,
            modifiedAt: new Date().getTime(),
          },
        };
      }

      const isToggleSelection = !!(
        action.modifiers?.metaKey || action.modifiers?.ctrlKey
      );
      const clickedCurrentSlide = action.slideId === state.template.currentRound;
      const hasMultiSelection = state.ui.multiSelectedSlides.length > 0;
      if (isToggleSelection && clickedCurrentSlide && hasMultiSelection) {
        const visibleSlideIds = new Set(
          getSlidesVisibleInSlideList(state.template.content.slides || []).map(
            slide => slide.id,
          ),
        );
        const remainingSelectedSlides = state.ui.multiSelectedSlides.filter(id =>
          visibleSlideIds.has(id),
        );
        const [nextCurrentRound, ...nextMultiSelectedSlides] = remainingSelectedSlides;
        if (!nextCurrentRound) {
          const clearedUI = deselectedUIState(state.ui);
          return {
            ...state,
            ui: { ...clearedUI, multiSelectedSlides: [] },
          };
        }
        const clearedUI = deselectedUIState(state.ui);
        return {
          ...state,
          ui: {
            ...clearedUI,
            multiSelectedSlides: nextMultiSelectedSlides,
          },
          template: {
            ...state.template,
            currentRound: nextCurrentRound,
            modifiedAt: new Date().getTime(),
          },
        };
      }

      const nextMultiSelectedSlides = getNextMultiSelectedSlides({
        template: state.template,
        currentMultiSelectedSlides: state.ui.multiSelectedSlides,
        clickedSlideId: action.slideId,
        modifiers: action.modifiers,
      });
      const clearedUI = deselectedUIState(state.ui);
      return {
        ...state,
        ui: {
          ...clearedUI,
          multiSelectedSlides: nextMultiSelectedSlides,
        },
      };
    }
    case DesignerActions.slideDragStarted: {
      if (!state.template) return state;
      const slideExists = state.template.content.slides?.some(
        slide => slide.id === action.slideId,
      );
      if (!slideExists) return state;
      const currentSelectedSlideId = state.template.currentRound;
      if (!currentSelectedSlideId || currentSelectedSlideId === action.slideId)
        return state;

      let nextMultiSelectedSlides = state.ui.multiSelectedSlides;
      if (state.ui.multiSelectedSlides.includes(action.slideId)) {
        const nextMultiSelectedSet = new Set(state.ui.multiSelectedSlides);
        nextMultiSelectedSet.delete(action.slideId);
        nextMultiSelectedSet.add(currentSelectedSlideId);
        nextMultiSelectedSlides = getSlidesVisibleInSlideList(
          state.template.content.slides || [],
        )
          .map(slide => slide.id)
          .filter(id => id !== action.slideId && nextMultiSelectedSet.has(id));
      } else if (state.ui.multiSelectedSlides.length > 0) {
        nextMultiSelectedSlides = [];
      }

      return {
        ...state,
        ui: { ...state.ui, multiSelectedSlides: nextMultiSelectedSlides },
        template: {
          ...state.template,
          currentRound: action.slideId,
          modifiedAt: new Date().getTime(),
        },
      };
    }

    case DesignerActions.elementsChanged: {
      if (!state.template) return state;
      const selectedSlide = getSelectedSlide(state.template);
      const slideToUse = action.slide || selectedSlide;
      if (!slideToUse) return state;

      let newSlide: Slide;

      // Z-ordering
      if (action.zChange && action.updatedElements) {
        let itemsCopy = [...slideToUse.elements];
        switch (action.zChange) {
          case 'up':
          case 'down': {
            const move = action.zChange === 'down' ? -1 : 1;
            for (const updatedElement of action.updatedElements) {
              const index = itemsCopy.findIndex(item => item.id === updatedElement.id);
              if (index !== -1) {
                const targetIndex = index + move;
                if (targetIndex >= 0 && targetIndex < itemsCopy.length) {
                  [itemsCopy[index], itemsCopy[targetIndex]] = [
                    itemsCopy[targetIndex],
                    itemsCopy[index],
                  ];
                }
              }
            }
            break;
          }
          case 'back': {
            const frontItems = action.updatedElements.sort(
              (a, b) =>
                itemsCopy.findIndex(item => item.id === a.id) -
                itemsCopy.findIndex(item => item.id === b.id),
            );
            itemsCopy = frontItems.concat(
              itemsCopy.filter(item => !frontItems.find(u => u.id === item.id)),
            );
            break;
          }
          case 'front': {
            const backItems = action.updatedElements.sort(
              (a, b) =>
                itemsCopy.findIndex(item => item.id === a.id) -
                itemsCopy.findIndex(item => item.id === b.id),
            );
            itemsCopy = itemsCopy
              .filter(item => !backItems.find(u => u.id === item.id))
              .concat(backItems);
            break;
          }
        }
        newSlide = { ...slideToUse, elements: itemsCopy };
      } else if (action.updatedElements) {
        // Element property updates
        let newPollOptions = slideToUse.pollOptions;
        const newElements = [...slideToUse.elements].map(e => {
          const updatedElement = action.updatedElements!.find(el => el.id === e.id);
          return updatedElement || e;
        });

        const updatesPollOptions = action.updatedElements.filter(e => e.isPollOption);
        if (updatesPollOptions.length > 0) {
          newPollOptions = [...(slideToUse.pollOptions || [])].map(oldPollOption => {
            const updatedPollOption = updatesPollOptions.find(
              el => el.id === oldPollOption.id,
            );
            if (updatedPollOption) {
              return {
                ...oldPollOption,
                displayName: updatedPollOption.textValue || oldPollOption.displayName,
                promptSourceId:
                  updatedPollOption.promptSourceId || oldPollOption.promptSourceId,
                ...(updatedPollOption.templatePlaceholderId && {
                  templatePlaceholderId: updatedPollOption.templatePlaceholderId,
                }),
              };
            }
            return oldPollOption;
          });
        }
        newSlide = { ...slideToUse, elements: newElements, pollOptions: newPollOptions };

        // Update selected elements if they are part of the new elements
        if (state.ui.selectedElements && !action.skipUpdateSelectedElements) {
          const newSelectedElements = state.ui.selectedElements.map(e => {
            const updatedElement = newElements.find(el => el.id === e.id);
            return updatedElement || e;
          });
          const slideElements = newSlide.elements.filter(e => !e.isPollOption);
          const newTemplate = state.template
            ? {
                ...state.template,
                content: {
                  ...state.template.content,
                  slides: (state.template.content.slides || []).map(s =>
                    s.id === newSlide.id ? { ...newSlide, elements: slideElements } : s,
                  ),
                },
                modifiedAt: new Date().getTime(),
              }
            : state.template;
          return {
            ...state,
            ui: { ...state.ui, selectedElements: newSelectedElements },
            template: newTemplate,
          };
        }
      } else {
        return state;
      }

      // Apply slide change to template (filter out poll option elements)
      const slideElements = newSlide!.elements.filter(e => !e.isPollOption);
      const newTemplate = state.template
        ? {
            ...state.template,
            content: {
              ...state.template.content,
              slides: (state.template.content.slides || []).map(s =>
                s.id === newSlide!.id ? { ...newSlide!, elements: slideElements } : s,
              ),
            },
            modifiedAt: new Date().getTime(),
          }
        : state.template;
      return { ...state, template: newTemplate };
    }

    case DesignerActions.elementsAdded: {
      if (!state.template) return state;
      const selectedSlide = getSelectedSlide(state.template);
      if (!selectedSlide) return state;
      if (action.elements.some(e => e.isPollOption)) return state;

      const existingElements = selectedSlide.elements || [];
      const newElements = [...existingElements, ...action.elements].filter(
        e => !action.elementsToRemove?.some(el => el.id === e.id),
      );
      const slideElements = newElements.filter(e => !e.isPollOption);
      const newTemplate = {
        ...state.template,
        content: {
          ...state.template.content,
          slides: (state.template.content.slides || []).map(s =>
            s.id === selectedSlide.id ? { ...selectedSlide, elements: slideElements } : s,
          ),
        },
        modifiedAt: new Date().getTime(),
      };

      // Clear all drag/transform state first, then select the new elements.
      // This prevents stale drag state (e.g. anElementIsBeingTransformed) from
      // carrying over and causing the new element to follow the cursor.
      const cleanUI = deselectedUIState(state.ui);
      const newUI = uiReducer(cleanUI, {
        type: DesignerActions.selectElements,
        selectedElements: action.elements,
      });

      return { ...state, template: newTemplate, ui: newUI };
    }

    case DesignerActions.elementsDeleted: {
      if (!state.template) return state;
      const selectedSlide = getSelectedSlide(state.template);
      if (!selectedSlide) return state;
      if (action.elements?.some(e => e?.lockedFromDeletion)) return state;
      if (
        action.elements?.some(e =>
          selectedSlide.contextElements?.some(
            ce => ce.purpose === 'question' && ce.elementId === e.id,
          ),
        )
      )
        return state;

      const deletedElements = action.elements.filter(e => !e?.isPollOption);
      const deletedPollOption = action.elements.filter(e => e?.isPollOption);

      const newElements = [...selectedSlide.elements].filter(
        e => !deletedElements.some(el => el.id === e.id),
      );
      const newContextElements = [...selectedSlide.contextElements].filter(
        e => !deletedElements.some(el => el.id === e.elementId),
      );

      let newSlide: Slide = {
        ...selectedSlide,
        elements: newElements,
        contextElements: newContextElements,
      };
      if (deletedPollOption.length > 0 && selectedSlide.pollOptions) {
        const firstPollOption = selectedSlide.pollOptions[0];
        const newPollOptions = [...(selectedSlide.pollOptions || [])].filter(
          p => !deletedPollOption.some(el => el.id === p.id),
        );
        newSlide = {
          ...newSlide,
          pollOptions: newPollOptions.length > 0 ? newPollOptions : [firstPollOption],
        };
      }
      for (const element of action.elements) {
        if (selectedSlide.type === 'canvas' && element.type === 'image') {
          const isDeletedElementBackgroundImagePreview = selectedSlide.contextElements.some(
            el => el.purpose === 'drawingBackground' && el.elementId === element.id,
          );
          if (isDeletedElementBackgroundImagePreview) {
            newSlide.backgroundDrawingFileId = undefined;
            newSlide.backgroundDrawingConfigFiles = undefined;
          }
        }
      }

      const slideElements = newSlide.elements.filter(e => !e.isPollOption);
      const newTemplate = {
        ...state.template,
        content: {
          ...state.template.content,
          slides: (state.template.content.slides || []).map(s =>
            s.id === newSlide.id ? { ...newSlide, elements: slideElements } : s,
          ),
        },
        modifiedAt: new Date().getTime(),
      };
      return { ...state, template: newTemplate, ui: deselectedUIState(state.ui) };
    }

    case DesignerActions.createNewSlide: {
      if (!state.template) return state;
      const selectedSlide = getSelectedSlide(state.template);
      const { combinedType, replace } = action;

      let newUI = deselectedUIState(state.ui);
      let newTemplate: TemplateState = state.template;

      if (combinedType === 'blank') {
        const newSlide = createNewEmptySlide({ type: 'slide' });
        newTemplate = addSlidesToTemplate(state.template, [newSlide], false);
        newUI = {
          ...newUI,
          selectedTab: {
            tab: 'addActivity',
            subTab: 'newBlankSlide',
            previousTab: newUI.selectedTab.tab,
          },
        };
      } else if (isSlideType(combinedType) || combinedType === 'slide') {
        const newSlide = createNewEmptySlide({
          type: 'slide',
          contentType: combinedType as SlideContentType,
          textValue: 'A curipod slide',
          textColor: theme.colors.blackPure,
          includeMedia: true,
        });
        newTemplate = addSlidesToTemplate(
          state.template,
          [newSlide],
          replace ? true : isEmptySlide(selectedSlide),
        );
        if (combinedType === 'fullscreen_media') {
          newUI = uiReducer(newUI, {
            type: DesignerActions.selectElements,
            selectedElements: [newSlide.elements[0]],
          });
        }
      } else if (combinedType === 'answers' || combinedType === 'canvas') {
        const newSlide = createNewEmptySlide({
          type: combinedType as SlideType,
          includeMedia: false,
          textColor: theme.colors.blackPure,
          textValue:
            combinedType === 'answers'
              ? 'Write your question'
              : 'Write drawing instructions',
        });
        const newVotingSlide = createNewEmptySlide({
          type: 'voting',
          textColor: theme.colors.blackPure,
          textValue: `Most relevant ${
            combinedType === 'canvas' ? 'drawings' : 'answers'
          }`,
          includeMedia: false,
        });
        const newPodiumSlide = createNewEmptySlide({
          type: 'podium',
          includeMedia: false,
        });
        newVotingSlide.dataReferenceSlideId = newSlide.id;
        newPodiumSlide.dataReferenceSlideId = newVotingSlide.id;
        newTemplate = addSlidesToTemplate(
          state.template,
          [newSlide, newVotingSlide, newPodiumSlide],
          replace || isEmptySlide(selectedSlide),
        );
      } else if (combinedType === 'aiAssessedAnswers') {
        const newSlide = createNewEmptySlide({
          type: combinedType as SlideType,
          textColor: theme.colors.blackPure,
          textValue: 'Write your question (AI feedback)',
          includeMedia: false,
        });
        const newGridSlide = createNewEmptySlide({ type: 'responsegrid' });
        newGridSlide.dataReferenceSlideId = newSlide.id;
        newTemplate = addSlidesToTemplate(
          state.template,
          [newSlide, newGridSlide],
          replace ? true : isEmptySlide(selectedSlide),
        );
      } else if (combinedType === 'aiAssessedImageAnswers') {
        const newSlide = createNewEmptySlide({
          type: combinedType as SlideType,
          textColor: theme.colors.blackPure,
          textValue: 'Write drawing instructions (AI feedback)',
          includeMedia: false,
        });
        const newGridSlide = createNewEmptySlide({ type: 'responsegrid' });
        newGridSlide.dataReferenceSlideId = newSlide.id;
        newTemplate = addSlidesToTemplate(
          state.template,
          [newSlide, newGridSlide],
          replace ? true : isEmptySlide(selectedSlide),
        );
      } else if (
        combinedType === 'wordcloud' ||
        combinedType === 'poll' ||
        combinedType === 'multipleChoice'
      ) {
        const newSlide = createNewEmptySlide({
          type: combinedType as SlideType,
          textColor: theme.colors.blackPure,
          textValue:
            combinedType === 'poll'
              ? 'Write a poll question'
              : combinedType === 'multipleChoice'
              ? 'The United States has 50 states.'
              : 'Write a word cloud topic',
          includeMedia: false,
        });
        newTemplate = addSlidesToTemplate(
          state.template,
          [newSlide],
          replace ? true : isEmptySlide(selectedSlide),
        );
      }

      if (newTemplate) newTemplate.modifiedAt = new Date().getTime();
      return { ...state, ui: newUI, template: newTemplate };
    }

    case DesignerActions.slideTypeChanged: {
      if (!state.template) return state;
      const selectedSlide = getSelectedSlide(state.template);
      if (!selectedSlide) return state;
      const { combinedType, replace } = action;

      let newTemplate: TemplateState = state.template;

      if (isSlideType(combinedType) || combinedType === 'slide') {
        const newSlide = createNewEmptySlide({
          type: 'slide',
          contentType: combinedType as SlideContentType,
        });
        newSlide.backgroundMedia = selectedSlide.backgroundMedia;
        newSlide.elements = selectedSlide.elements.filter(e => !e.isPollOption);
        newSlide.contextElements = [];
        newSlide.generatorId = selectedSlide.generatorId;
        newTemplate = addSlidesToTemplate(
          state.template,
          [newSlide],
          replace ? true : isEmptySlide(selectedSlide),
        );
      } else if (combinedType === 'answers' || combinedType === 'canvas') {
        const newSlide = createNewEmptySlide({ type: combinedType as SlideType });
        newSlide.backgroundMedia = selectedSlide.backgroundMedia;
        newSlide.elements = selectedSlide.elements;
        const newVotingSlide = createNewEmptySlide({
          type: 'voting',
          textValue: `Most relevant ${
            combinedType === 'canvas' ? 'drawings' : 'answers'
          }`,
        });
        const newPodiumSlide = createNewEmptySlide({ type: 'podium' });
        newVotingSlide.backgroundMedia = selectedSlide.backgroundMedia;
        newVotingSlide.dataReferenceSlideId = newSlide.id;
        newVotingSlide.elements = selectedSlide.elements;
        newPodiumSlide.dataReferenceSlideId = newVotingSlide.id;
        newPodiumSlide.backgroundMedia = selectedSlide.backgroundMedia;
        newPodiumSlide.elements = selectedSlide.elements;
        newSlide.contextElements = selectedSlide.contextElements;
        newSlide.generatorId = selectedSlide.generatorId;
        newTemplate = addSlidesToTemplate(
          state.template,
          [newSlide, newVotingSlide, newPodiumSlide],
          replace ? true : isEmptySlide(selectedSlide),
        );
      } else if (
        combinedType === 'aiAssessedAnswers' ||
        combinedType === 'aiAssessedImageAnswers'
      ) {
        const newSlide = createNewEmptySlide({ type: combinedType as SlideType });
        newSlide.backgroundMedia = selectedSlide.backgroundMedia;
        newSlide.elements = selectedSlide.elements;
        newSlide.generatorId = selectedSlide.generatorId;
        const newGridSlide = createNewEmptySlide({ type: 'responsegrid' });
        newGridSlide.dataReferenceSlideId = newSlide.id;
        newGridSlide.backgroundMedia = selectedSlide.backgroundMedia;
        newGridSlide.elements = selectedSlide.elements;
        newSlide.contextElements = selectedSlide.contextElements;
        newGridSlide.generatorId = selectedSlide.generatorId;
        newTemplate = addSlidesToTemplate(
          state.template,
          [newSlide, newGridSlide],
          replace ? true : isEmptySlide(selectedSlide),
        );
      } else if (
        combinedType === 'wordcloud' ||
        combinedType === 'poll' ||
        combinedType === 'multipleChoice'
      ) {
        const newSlide = createNewEmptySlide({ type: combinedType as SlideType });
        newSlide.backgroundMedia = selectedSlide.backgroundMedia;
        newSlide.elements = selectedSlide.elements;
        newSlide.contextElements = selectedSlide.contextElements;
        newSlide.generatorId = selectedSlide.generatorId;
        if (
          (combinedType === 'multipleChoice' || combinedType === 'poll') &&
          selectedSlide.pollOptions
        ) {
          // Converting to `poll` must strip MC-only metadata so we don't
          // silently carry an answer key into a non-quiz slide (and so a
          // future MC → poll → MC round-trip starts clean instead of
          // inheriting stale isCorrect/explanation flags).
          newSlide.pollOptions =
            combinedType === 'poll'
              ? selectedSlide.pollOptions.map(({ isCorrect, explanation, ...rest }) => {
                  void isCorrect;
                  void explanation;
                  return rest;
                })
              : selectedSlide.pollOptions;
        }
        newTemplate = addSlidesToTemplate(
          state.template,
          [newSlide],
          replace ? true : isEmptySlide(selectedSlide),
        );
      }

      if (newTemplate) newTemplate.modifiedAt = new Date().getTime();
      return { ...state, template: newTemplate };
    }

    default:
      return state;
  }
}

// Helper to add slides to template (mirrors the addSlide reducer logic)
function addSlidesToTemplate(
  template: TemplateState,
  newSlides: Slide[],
  replace: boolean,
): TemplateState {
  if (!template || !newSlides.length) return template;
  const selectedSlide = template.content.slides?.find(
    s => s.id === template.currentRound,
  );
  const existingSlides = [...(template.content.slides || [])];
  let slides = insertSlidesAfterSelectedSlide(
    existingSlides,
    newSlides,
    selectedSlide?.id,
  );
  if (replace && selectedSlide) {
    const relatedSlides = findRelatedSlidesWithoutIndex(selectedSlide.id, slides).map(
      s => s.id,
    );
    slides = slides.filter(s => !relatedSlides.includes(s.id));
  }
  return {
    ...template,
    currentRound: newSlides[0].id,
    content: { ...template.content, slides },
  };
}

// ─── Context ─────────────────────────────────────────────────────────────────

export type DesignerContextType = {
  state: DesignerState;
  dispatch: Dispatch<DesignerAction>;
};

const DesignerContext = createContext<DesignerContextType>({
  state: {
    ui: createInitialUIState({}),
    template: undefined,
    isFetchingTemplate: false,
    history: { canUndo: false, canRedo: false, undoStack: [], redoStack: [] },
  },
  dispatch: () => {
    /**/
  },
});

export function useDesignerContext<T>(selector: ContextSelector<DesignerContextType, T>) {
  return useContextSelector(DesignerContext, selector);
}

export const DesignerProvider = DesignerContext.Provider;

// ─── Selectors ───────────────────────────────────────────────────────────────

export const selectedSlideSelector = (ctx: DesignerContextType) => {
  const template = ctx.state.template;
  if (template?.content.slides?.length && template.currentRound) {
    return template.content.slides.find(r => r.id === template.currentRound);
  }
  return undefined;
};

export const selectedSlideIdSelector = (ctx: DesignerContextType) =>
  ctx.state.template?.currentRound;

export const multiSelectedSlideIdsSelector = (ctx: DesignerContextType) =>
  ctx.state.ui.multiSelectedSlides;

export const elementsToRenderSelector = (ctx: DesignerContextType) => {
  const selectedSlide = selectedSlideSelector(ctx);
  if (!selectedSlide) return [];
  if (selectedSlide.type === 'poll' || selectedSlide.type === 'multipleChoice') {
    const pollElements = createPollElements({
      pollOptions: selectedSlide.pollOptions || [],
      aspect: SLIDE_ASPECT,
    });
    return [...selectedSlide.elements, ...pollElements];
  }
  return selectedSlide.elements;
};

export const attachmentsSelector = (ctx: DesignerContextType) =>
  ctx.state.template?.attachments;

export const presentationIdSelector = (ctx: DesignerContextType) =>
  ctx.state.template?.id;

export const isOngoingGameModalVisibleSelector = (ctx: DesignerContextType) =>
  !!ctx.state.template?.content.activeGame?.isOngoing;

export const documentColorsSelector = (ctx: DesignerContextType) => {
  const template = ctx.state.template;
  if (!template?.content.slides) return [];
  const colors = template.content.slides.reduce((acc, slide) => {
    const slideColors = slide.elements.reduce((acc, element) => {
      if (element.type === 'svg' && element.colors) return [...acc, ...element.colors];
      if (element.type === 'shape' && element.colors) return [...acc, ...element.colors];
      if (isSlideElementText(element))
        return [...acc, ...findAllSlateTextColors(element.textValue)];
      return acc;
    }, [] as string[]);
    if (slide.backgroundMedia?.fileMimeType === 'image/color') {
      return [...acc, ...slideColors, slide.backgroundMedia.fileId];
    }
    return [...acc, ...slideColors];
  }, [] as string[]);
  return uniq(colors).filter(colorIsHexCode);
};

export const isGeneratedByAISelector = (ctx: DesignerContextType) => {
  const slides = ctx.state.template?.content.slides;
  if (!slides) return false;
  return slides.some(s => !!s.generatorId);
};

export const allPromptOutputsSelector = (
  ctx: DesignerContextType,
): Array<PromptOutput & { promptName: string; promptId: string }> => {
  const prompts = ctx.state.template?.aiContent?.prompts || [];
  return prompts.map(p => ({ ...p.promptOutput, promptName: p.name, promptId: p.id }));
};

// ─── Initial UI State ────────────────────────────────────────────────────────

export function createInitialUIState({
  initialPromptDependentInputs,
  initialLessonBuilding,
  activities,
}: {
  initialPromptDependentInputs?: PromptSingleTestCaseResult;
  initialLessonBuilding?: { params: PromptVariables };
  activities?: boolean;
}): DesignerUIState {
  let selectedTab: { tab: LeftMenuTabTypes };
  if (initialLessonBuilding) {
    selectedTab = { tab: 'agent' };
  } else if (activities) {
    selectedTab = { tab: 'none' };
  } else {
    selectedTab = { tab: 'interactiveElements' };
  }

  return {
    selectedTab,
    isDesktop: true,
    showMediaModal: false,
    isDrawerOpen: false,
    isLeftTabCollapsed: false,
    isSlideListCollapsed: false,
    zoom: 1,
    textElementIdBeingEdited: undefined,
    mcExplanationEditingOptionId: undefined,
    editClickPosition: undefined,
    elementTransformationProperties: undefined,
    anElementIsBeingTransformed: false,
    selectedTargetElements: [],
    canInteractWithIframe: false,
    previewAttachmentPassageElementId: undefined,
    showTabPaneFullWidth: false,
    selectedElements: null,
    multiSelectedSlides: [],
    selectedPromptId: undefined,
    richTextEditor: null,
    selectedRichTextEditors: [],
    isArrowKeyNavigationDisabled: false,
    testcaseResults: undefined,
    selectedTestcaseResult: undefined,
    forceLoadingState: undefined,
    isUserIdle: false,
    promptDependentInputs: initialPromptDependentInputs ?? {},
    agent: { isStreaming: false, error: null, isComplete: false, state: null },
    initialLessonBuilding: initialLessonBuilding
      ? { isLoading: true, started: false, params: initialLessonBuilding.params }
      : undefined,
    templateAccessErrorModal: { isVisible: false, errorType: null },
    copyLessonModal: { isVisible: false },
    isAutoCopyingLesson: false,
    isPresentOrPreviewModalVisible: false,
    isVariantPlaceholderLinkModalVisible: false,
    loaderStates: {},
  };
}

// ─── Exported helpers ────────────────────────────────────────────────────────

export function buildInitialLessonPromptVariablesQueryKey(presentationId: string) {
  return ['build-initial-lesson', 'prompt-variables', presentationId] as const;
}

export const createEmptyTemplate = ({
  workspaceId,
  parentFolderId,
  tenantId,
  metadata,
}: {
  workspaceId?: string;
  parentFolderId?: string;
  tenantId: string;
  metadata?: Template['metadata'];
}): Template => {
  return {
    id: NEW_TEMPLATE_PARAM,
    type: 'template',
    tenantId,
    llmThreadId: null,
    content: { rounds: [], slides: [], gameIds: [] },
    creator: { uid: 'undefined' },
    isPrivate: workspaceId ? false : true,
    parentId: parentFolderId ? parentFolderId : workspaceId ? workspaceId : null,
    members: [],
    modifiedAt: new Date().getTime(),
    createdAt: new Date().getTime(),
    coverImageId: undefined,
    metadata: metadata ?? undefined,
  };
};

// A blank lesson launched from the homepage carries the context the homepage
// already knew — subject, grade and the card that launched it — as query
// params. Seed the new template's metadata from them so downstream generators
// (which read `template.metadata.subject` / `.grade`) and analytics pick it up.
const asQueryString = (v: string | string[] | undefined): string | undefined =>
  typeof v === 'string' && v.length > 0 ? v : undefined;

function newTemplateMetadataFromQuery(
  query: ReturnType<typeof useRouter>['query'],
): Template['metadata'] {
  const subject = asQueryString(query.subject);
  const grade = asQueryString(query.grade);
  const selectedCardId = asQueryString(query.selectedCardId);
  if (!subject && !grade && !selectedCardId) return undefined;
  return {
    ...(subject ? { subject } : {}),
    ...(grade ? { grade } : {}),
    ...(selectedCardId ? { selectedCardId } : {}),
  };
}

// ─── Main Hook ───────────────────────────────────────────────────────────────

const getInitialPromptVariables = (client: QueryClient, presentationId: string) => {
  return client.getQueryData<PromptVariables>(
    buildInitialLessonPromptVariablesQueryKey(presentationId),
  );
};

// Actions that mutate template state and should be tracked by undo/redo
const TEMPLATE_MUTATING_ACTIONS = new Set<string>([
  ...Array.from(TEMPLATE_ONLY_ACTIONS),
  DesignerActions.slideSelected,
  DesignerActions.createNewSlide,
  DesignerActions.slideTypeChanged,
  DesignerActions.elementsChanged,
  DesignerActions.elementsAdded,
  DesignerActions.elementsDeleted,
  DesignerActions.selectElements,
]);

// Actions that should skip undo history even though they mutate template
const SKIP_HISTORY_ACTIONS = new Set<string>([
  DesignerActions.initTemplate,
  DesignerActions.initSetFirstSlide,
  DesignerActions.initActiveGame,
  DesignerActions.agentUpdatedState,
]);

export const useDesignerState = (templateId: string) => {
  const router = useRouter();
  const { tenantId, workspaceId, parentFolderId } = useRouterMatch();
  const client = useQueryClient();
  const isDesktop = useBreakPoint(MOBILE_TO_DESKTOP_BREAKPOINT_PX);

  useHideIntercom();

  const { accessBadge: userAccessBadge } = useAccessBadge();
  const {
    template: serverTemplate,
    isFetchingTemplate,
    error: errorFetchingTemplate,
  } = useRawTemplate(templateId, tenantId);

  // ─── UI init params ──────────────────────────────────────────────────
  const promptDependentInputsCache = client.getQueryData<PromptSingleTestCaseResult>([
    'persist',
    'prompt-inputs',
    templateId,
  ]);
  const buildInitialLesson = router.query.buildInitialLesson;
  const activities = router.query.activities;

  // ─── Reducer ──────────────────────────────────────────────────────────
  const initialState: DesignerState = {
    ui: createInitialUIState({
      initialPromptDependentInputs: promptDependentInputsCache,
      initialLessonBuilding:
        buildInitialLesson === 'true' && getInitialPromptVariables(client, templateId)
          ? { params: getInitialPromptVariables(client, templateId) as PromptVariables }
          : undefined,
      activities: activities === 'true' ? true : false,
    }),
    template:
      !templateId || templateId === NEW_TEMPLATE_PARAM
        ? createEmptyTemplate({
            workspaceId,
            parentFolderId,
            tenantId,
            metadata: newTemplateMetadataFromQuery(router.query),
          })
        : undefined,
    isFetchingTemplate: false,
    history: { canUndo: false, canRedo: false, undoStack: [], redoStack: [] },
  };

  const [state, dispatch] = useReducer(designerReducer, initialState);

  // ─── Clean up query params ────────────────────────────────────────────
  useEffect(() => {
    if (buildInitialLesson && router.asPath.includes('buildInitialLesson')) {
      router.replace(router.asPath.replace('buildInitialLesson=true', ''));
    }
  }, [buildInitialLesson, router]);

  useEffect(() => {
    if (activities && router.asPath.includes('activities')) {
      dispatch({ type: DesignerActions.setSelectedTab, payload: { tab: 'none' } });
      router.replace(router.asPath.replace('activities=true', ''));
    }
  }, [activities, router, dispatch]);

  // ─── Sync isDesktop ───────────────────────────────────────────────────
  useEffect(() => {
    dispatch({ type: DesignerActions.setIsDesktop, payload: isDesktop });
  }, [isDesktop, dispatch]);

  // ─── Template access ──────────────────────────────────────────────────
  const isTemplateOwner = useMemo(
    () => serverTemplate && userAccessBadge?.user.id === serverTemplate?.creator.uid,
    [userAccessBadge, serverTemplate],
  );

  // When enabled for the user's org, a member can edit any lesson that lives in the shared
  // WORKSPACE (isPrivate === false). Treat such a flagged member as owner-equivalent so
  // they get the authenticated (editable) fetch and skip the forced "make a copy" modal.
  // A lesson in another user's private "My Lessons" is NOT covered — the flag only applies
  // to workspace lessons; anything else falls back to the copy flow.
  // Gate on serverTemplate being loaded: if the private fetch failed (deleted /
  // not-found / unfetchable), leave hasEditAccess false so useTemplateAccessHandler
  // still runs its public-fallback + access-error recovery instead of rendering a
  // stuck, empty designer.
  const canEditAnyLesson = useFeatureFlag('org_members_can_edit_any_lesson');
  const hasEditAccess =
    !!serverTemplate &&
    (isTemplateOwner === true ||
      (canEditAnyLesson && serverTemplate.isPrivate === false));

  // S&S auto-copy must not duplicate a lesson the user can edit directly, and
  // duplication can't be undone — so it needs a *resolved* verdict, not the
  // in-flight `false` that `hasEditAccess` reads while the template fetch or
  // the Unleash flags (async client context) are still loading. `undefined`
  // tells the hook to hold off.
  //
  // Crucially, the flag only matters when the user managed to fetch someone
  // else's WORKSPACE lesson privately — i.e. curipod org members. For everyone
  // else no flag value can grant edit access, so their verdict resolves as soon
  // as the template fetch settles and a slow (or never-loading) Unleash can't
  // block their S&S links. The remaining internal-only wait settles on
  // `flagsReady` or `flagsError` — Unleash reports fetch failures, so a broken
  // flag proxy degrades to flag-off behavior instead of hanging.
  const { flagsReady, flagsError } = useFlagsStatus();
  const flagsSettled = flagsReady || !!flagsError;
  const flagCouldGrantAccess =
    isTemplateOwner !== true && !!serverTemplate && serverTemplate.isPrivate === false;
  const hasEditAccessResolved: boolean | undefined =
    isFetchingTemplate || (flagCouldGrantAccess && !flagsSettled)
      ? undefined
      : hasEditAccess;

  const { accessError, publicTemplate } = useTemplateAccessHandler({
    templateId,
    isTemplateOwner: hasEditAccess,
    privateTemplateError: errorFetchingTemplate as import('axios').AxiosError | null,
    template: serverTemplate,
    isFetchingTemplate,
  });

  useEffect(() => {
    if (accessError) {
      dispatch({
        type: DesignerActions.showTemplateAccessErrorModal,
        errorType: accessError,
      });
    }
  }, [accessError, dispatch]);

  const handleAutoCopyingChange = useCallback(
    (isAutoCopying: boolean) =>
      dispatch({ type: DesignerActions.setIsAutoCopyingLesson, payload: isAutoCopying }),
    [dispatch],
  );
  const publicTemplateMetadata = publicTemplate?.metadata;
  const handleAutoCopyFallbackToModal = useCallback(
    (stampMetadata: Template['metadata']) =>
      dispatch({
        type: DesignerActions.showCopyLessonModal,
        payload: { templateMetadata: publicTemplateMetadata, stampMetadata },
      }),
    [dispatch, publicTemplateMetadata],
  );

  const { isScopeAndSequenceAutoCopyLink } = useScopeAndSequenceAutoCopy({
    templateId,
    tenantId,
    hasEditAccess: hasEditAccessResolved,
    publicTemplate,
    isUserLoaded: userAccessBadge !== undefined,
    onAutoCopyingChange: handleAutoCopyingChange,
    onFallbackToModal: handleAutoCopyFallbackToModal,
  });

  // On an S&S link the outcome is undecided until flags/template/badge resolve:
  // either auto-copy fires (redirecting to a fresh duplicate — any local edits
  // would be discarded) or edit access lets the user straight in. The edit page
  // isn't SSR-bootstrapped with flags, so this window is real on direct loads.
  // Keep the blocking auto-copy overlay up for the whole window; it drops when
  // edit access resolves true, or when a fallback/access-error modal takes over.
  const isAwaitingAutoCopyDecision =
    isScopeAndSequenceAutoCopyLink === true &&
    hasEditAccessResolved !== true &&
    !state.ui.copyLessonModal.isVisible &&
    !state.ui.templateAccessErrorModal.isVisible;
  useEffect(() => {
    if (isAwaitingAutoCopyDecision !== !!state.ui.isAutoCopyingLesson) {
      dispatch({
        type: DesignerActions.setIsAutoCopyingLesson,
        payload: isAwaitingAutoCopyDecision,
      });
    }
  }, [isAwaitingAutoCopyDecision, state.ui.isAutoCopyingLesson, dispatch]);

  useEffect(() => {
    if (hasEditAccess === true) {
      // `canEditAnyLesson` resolves asynchronously (client Unleash context; the edit
      // page isn't SSR-bootstrapped), so on a direct load/refresh the copy modal can be
      // shown before edit access is granted. Dismiss it once the flag flips true, or a
      // flagged member is stuck on the copy modal with no path to edit the original.
      if (state.ui.copyLessonModal.isVisible) {
        dispatch({ type: DesignerActions.hideCopyLessonModal });
      }
      return;
    }
    // Scope & sequence links copy the lesson automatically — no modal. While
    // the router hasn't parsed the query yet the value is `undefined`
    // ("unknown"), and the modal must hold off until it's an explicit `false`
    // so it can't open before auto-copy gets a chance to run.
    if (isScopeAndSequenceAutoCopyLink !== false) return;
    if (!!publicTemplate && userAccessBadge !== undefined) {
      dispatch({
        type: DesignerActions.showCopyLessonModal,
        payload: { templateMetadata: publicTemplate.metadata },
      });
    }
  }, [
    publicTemplate,
    hasEditAccess,
    dispatch,
    userAccessBadge,
    isScopeAndSequenceAutoCopyLink,
    state.ui.copyLessonModal.isVisible,
  ]);

  // ─── Template sync effects ────────────────────────────────────────────
  useEffect(() => {
    // sync localtemplate after the first save
    if (
      state.template &&
      state.template.id === NEW_TEMPLATE_PARAM &&
      serverTemplate?.id
    ) {
      dispatch({
        type: DesignerActions.initTemplate,
        template: {
          ...serverTemplate,
          content: { ...state.template.content },
          currentRound: state.template.currentRound,
        },
        skipHistory: true,
      });
      return;
    }
  }, [state.template, serverTemplate, dispatch]);

  // Init template from server when local template doesn't exist yet
  const hasLocalTemplate = !!state.template;
  useEffect(() => {
    if (serverTemplate && !hasLocalTemplate) {
      dispatch({
        type: DesignerActions.initTemplate,
        template: serverTemplate,
        skipHistory: true,
      });
      dispatch({
        type: DesignerActions.setForceLoadingState,
        payload: undefined,
        skipHistory: true,
      });
    }
  }, [serverTemplate, hasLocalTemplate, dispatch]);

  // Re-sync from server after tab-sync recovery (forceLoadingState === 'success')
  const forceLoadingState = state.ui.forceLoadingState;
  const isAgentBusy =
    state.ui.initialLessonBuilding?.isLoading || state.ui.agent.isStreaming;
  useEffect(() => {
    if (
      serverTemplate &&
      !isFetchingTemplate &&
      forceLoadingState === 'success' &&
      !isAgentBusy
    ) {
      dispatch({
        type: DesignerActions.initTemplate,
        template: serverTemplate,
        skipHistory: true,
      });
      dispatch({
        type: DesignerActions.setForceLoadingState,
        payload: undefined,
        skipHistory: true,
      });
    }
  }, [serverTemplate, isFetchingTemplate, forceLoadingState, isAgentBusy, dispatch]);

  // Sync active game state from server
  const localActiveGame = state.template?.content?.activeGame;
  const serverActiveGame = serverTemplate?.content?.activeGame;
  useEffect(() => {
    if (serverActiveGame && !isEqual(serverActiveGame, localActiveGame)) {
      dispatch({
        type: DesignerActions.initActiveGame,
        activeGame: serverActiveGame,
        skipHistory: true,
      });
    }
  }, [serverActiveGame, localActiveGame, dispatch]);

  // ─── First slide selection ────────────────────────────────────────────
  const selectedSlide = useMemo(() => getSelectedSlide(state.template), [state.template]);

  useEffect(() => {
    if (
      !selectedSlide &&
      state.template &&
      state.template.content.slides &&
      state.template.content.slides.length > 0
    ) {
      dispatch({ type: DesignerActions.initSetFirstSlide, skipHistory: true });
    }
  }, [state.template, selectedSlide, dispatch]);

  // ─── Loading slide injection ──────────────────────────────────────────
  useEffect(() => {
    if (
      state.ui.initialLessonBuilding?.isLoading &&
      state.template &&
      (!state.template.content.slides || state.template.content.slides.length === 0)
    ) {
      dispatch({
        type: DesignerActions.addSlide,
        slides: [LOADING_SLIDE],
        replace: false,
        skipHistory: true,
      });
    }
  }, [state.ui.initialLessonBuilding?.isLoading, state.template, dispatch]);

  // ─── Loading slide removal ────────────────────────────────────────────
  useEffect(() => {
    if (
      !state.ui.initialLessonBuilding?.isLoading &&
      state.template?.content.slides?.some(s => s.id === LOADING_SLIDE_ID)
    ) {
      const slidesWithoutLoader = state.template.content.slides!.filter(
        s => s.id !== LOADING_SLIDE_ID,
      );
      const firstSlide = slidesWithoutLoader[0];
      if (firstSlide) {
        dispatch({
          type: DesignerActions.allSlidesChanged,
          slides: slidesWithoutLoader,
          selectedSlideId: firstSlide.id,
          skipHistory: true,
        });
      }
    }
  }, [state.ui.initialLessonBuilding?.isLoading, state.template, dispatch]);

  // ─── Tab auto-switching on slide change ───────────────────────────────
  useEffect(() => {
    if (!selectedSlide) return;
    const isActivitySlide = (slideType: SlideType) =>
      [
        'answers',
        'aiAssessedAnswers',
        'aiAssessedImageAnswers',
        'canvas',
        'wordcloud',
        'poll',
        'podium',
      ].includes(slideType);

    const newSlideIsActivity = isActivitySlide(selectedSlide.type);
    const tabsToNotInterrupt: LeftMenuTabTypes[] = [
      'prompts',
      'testCases',
      'testCasesResult',
    ];
    const isOnProtectedTab = tabsToNotInterrupt.includes(state.ui.selectedTab.tab);

    if (
      newSlideIsActivity &&
      state.ui.isDesktop &&
      !isOnProtectedTab &&
      !state.ui.initialLessonBuilding?.isLoading
    ) {
      dispatch({
        type: DesignerActions.setSelectedTab,
        payload: { tab: 'interactiveElements' },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlide?.id]);

  // ─── Handle lesson generation complete ────────────────────────────────
  useEffect(() => {
    if (state.ui.agent.isComplete && state.ui.initialLessonBuilding?.isLoading) {
      if (!selectedSlide) return;
      dispatch({
        type: DesignerActions.setSelectedTab,
        payload: { tab: 'interactiveElements' },
      });
      dispatch({ type: DesignerActions.completeInitialLessonBuilding });
    }
  }, [
    state.ui.agent.isComplete,
    state.ui.initialLessonBuilding?.isLoading,
    selectedSlide,
  ]);

  // ─── Persist prompt inputs on unmount ─────────────────────────────────
  const stateRef = useRef<DesignerState>();
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    return () => {
      const cache = stateRef.current?.ui.promptDependentInputs;
      if (stateRef.current && templateId) {
        client.setQueryData(
          ['persist', 'prompt-inputs', templateId],
          (existing: PromptSingleTestCaseResult | undefined) => {
            if (!existing) return cache;
            return { ...existing, ...cache };
          },
        );
      }
    };
  }, [client, templateId]);

  // Merge isFetchingTemplate from the fetch hook into state
  const mergedState = useMemo(
    () => ({
      ...state,
      isFetchingTemplate,
    }),
    [state, isFetchingTemplate],
  );

  return {
    state: mergedState,
    dispatch,
  };
};
